import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { ArrowUpIcon, PlusIcon } from '@heroicons/react/24/solid';
import {
  BarsArrowUpIcon,
  CalendarDaysIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EllipsisVerticalIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import 'react-datepicker/dist/react-datepicker.css';
import { transactionsAPI, categoriesAPI } from '../services/api';
import StatusToast from './StatusToast';
import SearchableSelect from './SearchableSelect';
import ManualTransactionModal from './ManualTransactionModal';
import HistoricalImportModal from './HistoricalImportModal';
import usePersistentUiState from '../hooks/usePersistentUiState';
import {
  buildHistoricalImportPayload,
  buildHistoricalImportPreview,
} from '../utils/historicalImport';
import {
  getTodayBusinessIsoDate,
  parseBusinessDate,
  toBusinessIsoDate,
} from '../utils/businessDate';

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const ADD_CATEGORY_OPTION = '__add_category__';
const DEFAULT_NEW_CATEGORY_COLOR = '#3b82f6';
const CATEGORY_COLOR_OPTIONS = [
  { name: 'Green', value: '#10b981' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Purple', value: '#8b5cf6' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Orange', value: '#f59e0b' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Indigo', value: '#6366f1' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Teal', value: '#14b8a6' },
  { name: 'Gray', value: '#64748b' },
];
const UI_STATE_KEY = 'receipt-tracker:ui:transactions:v2';
const DELETE_UNDO_MS = 5000;
const RECEIPT_DETAIL_STORAGE_KEY = 'receipt-tracker:receipt-detail:v1';
const RECURRENCE_FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi-weekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semiannual', label: 'Semiannual' },
  { value: 'annual', label: 'Annual' },
];
const RECURRENCE_FREQUENCY_LABELS = RECURRENCE_FREQUENCY_OPTIONS.reduce((labels, option) => {
  labels[option.value] = option.label;
  return labels;
}, {});

const toComparableText = (value) => String(value || '').toLowerCase().trim();
const normalizeHexColor = (value) => String(value || '').trim().toLowerCase();
const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const TRANSACTION_SORT_OPTIONS = ['latest', 'oldest', 'lowest', 'highest'];
const TRANSACTION_SEARCH_FILTER_OPTIONS = ['amount', 'date', 'category', 'payment'];

const buildDefaultTransactionsUiState = () => ({
  searchText: '',
  searchAmountMin: '',
  searchAmountMax: '',
  filterDateFrom: '',
  filterDateTo: '',
  filterCategory: '',
  filterPaymentMethod: '',
  showSearchBar: false,
  showSearchFilters: false,
  activeSearchFilter: 'amount',
  sortOrder: 'latest',
  activeTransactionsTab: 'ledger',
  expandedReceipts: {},
  showAllItemsByReceipt: {},
  expandedUpcomingRecurringByTemplate: {},
});

const sanitizeTransactionsUiState = (value, fallbackState = buildDefaultTransactionsUiState()) => {
  const nextState = isPlainObject(value) ? value : {};
  const restoredSearchText = String(nextState.searchText || nextState.searchStore || nextState.searchItem || '');
  const searchAmountMin = String(nextState.searchAmountMin || '');
  const searchAmountMax = String(nextState.searchAmountMax || '');
  const filterDateFrom = String(nextState.filterDateFrom || '');
  const filterDateTo = String(nextState.filterDateTo || '');
  const filterCategory = String(nextState.filterCategory || '');
  const filterPaymentMethod = String(nextState.filterPaymentMethod || '');
  const hasActiveSearchInputs = Boolean(
    restoredSearchText
    || searchAmountMin
    || searchAmountMax
    || filterDateFrom
    || filterDateTo
    || filterCategory
    || filterPaymentMethod
  );

  return {
    ...fallbackState,
    searchText: restoredSearchText,
    searchAmountMin,
    searchAmountMax,
    filterDateFrom,
    filterDateTo,
    filterCategory,
    filterPaymentMethod,
    showSearchBar: Boolean(nextState.showSearchBar || hasActiveSearchInputs),
    showSearchFilters: Boolean(nextState.showSearchFilters),
    activeSearchFilter: TRANSACTION_SEARCH_FILTER_OPTIONS.includes(String(nextState.activeSearchFilter || ''))
      ? String(nextState.activeSearchFilter)
      : fallbackState.activeSearchFilter,
    sortOrder: TRANSACTION_SORT_OPTIONS.includes(String(nextState.sortOrder || ''))
      ? String(nextState.sortOrder)
      : fallbackState.sortOrder,
    activeTransactionsTab: String(nextState.activeTransactionsTab || '') === 'upcoming' ? 'upcoming' : 'ledger',
    expandedReceipts: isPlainObject(nextState.expandedReceipts) ? nextState.expandedReceipts : {},
    showAllItemsByReceipt: isPlainObject(nextState.showAllItemsByReceipt) ? nextState.showAllItemsByReceipt : {},
    expandedUpcomingRecurringByTemplate: isPlainObject(nextState.expandedUpcomingRecurringByTemplate)
      ? nextState.expandedUpcomingRecurringByTemplate
      : {},
  };
};

const hslToHex = (h, s, l) => {
  const saturation = s / 100;
  const lightness = l / 100;
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const x = chroma * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lightness - chroma / 2;
  let r = 0;
  let g = 0;
  let b = 0;

  if (h < 60) {
    r = chroma; g = x; b = 0;
  } else if (h < 120) {
    r = x; g = chroma; b = 0;
  } else if (h < 180) {
    r = 0; g = chroma; b = x;
  } else if (h < 240) {
    r = 0; g = x; b = chroma;
  } else if (h < 300) {
    r = x; g = 0; b = chroma;
  } else {
    r = chroma; g = 0; b = x;
  }

  const toHex = (channel) => Math.round((channel + m) * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const getNextUniqueCategoryColor = (takenColors, palette = CATEGORY_COLOR_OPTIONS) => {
  const normalizedTaken = new Set(Array.from(takenColors || []).map(normalizeHexColor));
  const paletteColor = palette.find((color) => !normalizedTaken.has(normalizeHexColor(color.value)));
  if (paletteColor) {
    return paletteColor.value;
  }

  for (let hue = 0; hue < 360; hue += 11) {
    const generated = hslToHex(hue, 70, 52);
    if (!normalizedTaken.has(normalizeHexColor(generated))) {
      return generated;
    }
  }

  return DEFAULT_NEW_CATEGORY_COLOR;
};

const parseTransactionDate = (value) => parseBusinessDate(value);
const toIsoDate = (value) => toBusinessIsoDate(value);
const getTodayIsoDate = () => getTodayBusinessIsoDate();

const hasActiveFilters = (filters) => {
  return Object.values(filters).some((value) => String(value || '').trim() !== '');
};

const buildReceiptKey = (transaction) => {
  if (transaction.receiptImageUrl) {
    return `receipt:${transaction.receiptImageUrl}`;
  }

  const fallbackDate = toIsoDate(transaction.transactionDate) || 'unknown-date';
  const fallbackStore = toComparableText(transaction.storeName) || 'unknown-store';
  const fallbackLocation = toComparableText(transaction.storeLocation) || 'unknown-location';
  const fallbackPayment = toComparableText(transaction.paymentMethod) || 'unknown-payment';

  return `meta:${fallbackDate}|${fallbackStore}|${fallbackLocation}|${fallbackPayment}`;
};

const persistReceiptDetailSelection = (receipt) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (!receipt?.key) {
      window.sessionStorage.removeItem(RECEIPT_DETAIL_STORAGE_KEY);
      return;
    }
    window.sessionStorage.setItem(
      RECEIPT_DETAIL_STORAGE_KEY,
      JSON.stringify({
        key: String(receipt.key),
        receipt,
        savedAt: Date.now(),
      })
    );
  } catch {
    // Ignore storage errors and continue with route navigation.
  }
};

const groupTransactionsByReceipt = (transactions) => {
  const groups = new Map();

  transactions.forEach((transaction) => {
    const key = buildReceiptKey(transaction);
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        receiptImageUrl: transaction.receiptImageUrl || '',
        transactionDate: transaction.transactionDate,
        storeName: transaction.storeName,
        storeLocation: transaction.storeLocation,
        paymentMethod: transaction.paymentMethod,
        taxAmount: toNumber(transaction.taxAmount),
        hasTaxLineItem: false,
        items: [],
        totalAmount: 0,
      });
    }

    const group = groups.get(key);
    group.items.push(transaction);
    group.totalAmount = Number((group.totalAmount + toNumber(transaction.itemTotal)).toFixed(2));
    if (/\btax\b/i.test(String(transaction.itemName || ''))) {
      group.hasTaxLineItem = true;
    }

    const currentDate = parseTransactionDate(group.transactionDate).getTime();
    const transactionDate = parseTransactionDate(transaction.transactionDate).getTime();
    if (Number.isFinite(transactionDate) && (!Number.isFinite(currentDate) || transactionDate > currentDate)) {
      group.transactionDate = transaction.transactionDate;
    }
  });

  return Array.from(groups.values());
};

const createManualItem = () => ({
  itemName: '',
  itemTotal: '',
  categoryId: '',
  quantity: '',
  unitPrice: '',
  itemTotalOverridden: false,
});
const createEmptyManualFieldErrors = () => ({
  transactionDate: '',
  storeName: '',
  paymentMethod: '',
  taxAmount: '',
  items: {},
});

const getAutoCalculatedItemTotal = (item) => {
  const quantity = item?.quantity === '' ? 1 : Number(item?.quantity);
  const unitPrice = Number(item?.unitPrice);
  if (Number.isFinite(quantity) && quantity > 0 && Number.isFinite(unitPrice) && unitPrice >= 0) {
    return Number((quantity * unitPrice).toFixed(2));
  }

  return null;
};

const resolveManualItemTotal = (item) => {
  const manualTotal = Number(item?.itemTotal);
  const autoCalculatedTotal = getAutoCalculatedItemTotal(item);

  if (item?.itemTotalOverridden) {
    if (Number.isFinite(manualTotal) && manualTotal > 0) {
      return Number(manualTotal.toFixed(2));
    }
    return null;
  }

  if (autoCalculatedTotal !== null) {
    return autoCalculatedTotal;
  }

  if (Number.isFinite(manualTotal) && manualTotal > 0) {
    return Number(manualTotal.toFixed(2));
  }

  return null;
};

const formatRecurrencePreviewDate = (value) => {
  const parsed = parseTransactionDate(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatBillingMonth = (value) => {
  const parsed = parseTransactionDate(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  });
};

const formatBillDueDate = (value) => {
  const parsed = parseTransactionDate(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  return parsed.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatRecurrenceRange = (startValue, endValue) => {
  const startMonth = formatBillingMonth(startValue);
  const endMonth = formatBillingMonth(endValue);

  if (startMonth && endMonth) {
    return `${startMonth} to ${endMonth}`;
  }
  if (startMonth) {
    return `${startMonth} onward`;
  }
  return '';
};

function TransactionList() {
  const [persistedUiState, setPersistedUiState] = usePersistentUiState(
    UI_STATE_KEY,
    buildDefaultTransactionsUiState,
    { storage: 'local', validate: sanitizeTransactionsUiState }
  );
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedReceipts, setExpandedReceipts] = useState(() => persistedUiState.expandedReceipts);
  const [showAllItemsByReceipt, setShowAllItemsByReceipt] = useState(() => persistedUiState.showAllItemsByReceipt);
  const [updatingTransactionId, setUpdatingTransactionId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeAddMenu, setActiveAddMenu] = useState(null);
  const [activeTransactionActionId, setActiveTransactionActionId] = useState('');
  const [savedTransactionId, setSavedTransactionId] = useState('');
  const [pendingDeletes, setPendingDeletes] = useState([]);
  const [pendingDeleteToast, setPendingDeleteToast] = useState(null);
  const [showManualModal, setShowManualModal] = useState(false);
  const [showHistoricalImportModal, setShowHistoricalImportModal] = useState(false);
  const [showCreateCategoryModal, setShowCreateCategoryModal] = useState(false);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [newCategoryForm, setNewCategoryForm] = useState({
    name: '',
    parentCategoryId: '',
  });
  const [isCreateCategoryNameFocused, setIsCreateCategoryNameFocused] = useState(false);
  const [isCreateCategorySelectedFromSuggestion, setIsCreateCategorySelectedFromSuggestion] = useState(false);
  const [newParentSubcategories, setNewParentSubcategories] = useState([]);
  const [newSubcategoryInput, setNewSubcategoryInput] = useState('');
  const [showAllCreateSubcategoryRefs, setShowAllCreateSubcategoryRefs] = useState(false);
  const [createCategoryContext, setCreateCategoryContext] = useState({
    source: 'transaction',
    transactionId: '',
    manualItemIndex: null,
    receiptItemId: '',
  });
  const [creatingTransaction, setCreatingTransaction] = useState(false);
  const [showManualDuplicateConfirm, setShowManualDuplicateConfirm] = useState(false);
  const [importingHistorical, setImportingHistorical] = useState(false);
  const [historicalImportPreview, setHistoricalImportPreview] = useState(null);
  const [historicalImportFileName, setHistoricalImportFileName] = useState('');
  const [historicalImportFallbackCategoryId, setHistoricalImportFallbackCategoryId] = useState('');
  const [isManualDetailsOpen, setIsManualDetailsOpen] = useState(false);
  const [editingReceiptKey, setEditingReceiptKey] = useState('');
  const [editingReceiptDraft, setEditingReceiptDraft] = useState({
    transactionDate: '',
    storeName: '',
    storeLocation: '',
    paymentMethod: '',
    items: [],
  });
  const [editingReceiptStep, setEditingReceiptStep] = useState(1);
  const [savingReceiptDraft, setSavingReceiptDraft] = useState(false);
  const [isMobileView, setIsMobileView] = useState(() => window.innerWidth < 768);
  const [showMobileAddButton, setShowMobileAddButton] = useState(() => window.scrollY > 120);
  const [showScrollTopButton, setShowScrollTopButton] = useState(false);
  const [showFloatingTransactionsHeader, setShowFloatingTransactionsHeader] = useState(false);
  const topAddMenuRef = useRef(null);
  const floatingAddMenuRef = useRef(null);
  const sortMenuRef = useRef(null);
  const manualModalRef = useRef(null);
  const historicalImportModalRef = useRef(null);
  const historicalImportInputRef = useRef(null);
  const lastScrollYRef = useRef(window.scrollY);
  const deleteTimeoutsRef = useRef(new Map());
  const savedHighlightTimeoutRef = useRef(null);
  const [manualForm, setManualForm] = useState({
    transactionDate: getTodayIsoDate(),
    storeName: '',
    storeLocation: '',
    taxAmount: '',
    paymentMethod: '',
    isRecurring: false,
    recurrenceFrequency: '',
    recurrenceStartDate: '',
    recurrenceEndDate: '',
    items: [createManualItem()],
  });
  const [manualFormError, setManualFormError] = useState('');
  const [manualFieldErrors, setManualFieldErrors] = useState(createEmptyManualFieldErrors);
  const [manualModalTitle, setManualModalTitle] = useState('Add Transaction');

  const [searchText, setSearchText] = useState(() => persistedUiState.searchText);
  const [searchTextInput, setSearchTextInput] = useState(() => persistedUiState.searchText);
  const [searchAmountMin, setSearchAmountMin] = useState(() => persistedUiState.searchAmountMin);
  const [searchAmountMax, setSearchAmountMax] = useState(() => persistedUiState.searchAmountMax);
  const [filterDateFrom, setFilterDateFrom] = useState(() => persistedUiState.filterDateFrom);
  const [filterDateTo, setFilterDateTo] = useState(() => persistedUiState.filterDateTo);
  const [filterCategory, setFilterCategory] = useState(() => persistedUiState.filterCategory);
  const [filterPaymentMethod, setFilterPaymentMethod] = useState(() => persistedUiState.filterPaymentMethod);
  const [showSearchBar, setShowSearchBar] = useState(() => persistedUiState.showSearchBar);
  const [showSearchFilters, setShowSearchFilters] = useState(() => persistedUiState.showSearchFilters);
  const [activeSearchFilter, setActiveSearchFilter] = useState(() => persistedUiState.activeSearchFilter);
  const [sortOrder, setSortOrder] = useState(() => persistedUiState.sortOrder);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [activeTransactionsTab, setActiveTransactionsTab] = useState(() => persistedUiState.activeTransactionsTab);
  const [upcomingRecurring, setUpcomingRecurring] = useState([]);
  const [upcomingRecurringLoading, setUpcomingRecurringLoading] = useState(false);
  const [upcomingRecurringLoadError, setUpcomingRecurringLoadError] = useState('');
  const [hasLoadedUpcomingRecurring, setHasLoadedUpcomingRecurring] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [lastLoadedTransactionCount, setLastLoadedTransactionCount] = useState(null);
  const [expandedUpcomingRecurringByTemplate, setExpandedUpcomingRecurringByTemplate] = useState(
    () => persistedUiState.expandedUpcomingRecurringByTemplate
  );
  const [editingRecurringTemplateId, setEditingRecurringTemplateId] = useState('');
  const [editingRecurringAmount, setEditingRecurringAmount] = useState('');
  const [editingRecurringFrequency, setEditingRecurringFrequency] = useState('');
  const [editingRecurringEndDate, setEditingRecurringEndDate] = useState('');
  const [editingRecurringApplyFromDate, setEditingRecurringApplyFromDate] = useState('');
  const [editingRecurringTransactionDate, setEditingRecurringTransactionDate] = useState('');
  const [savingRecurringTemplateId, setSavingRecurringTemplateId] = useState('');
  const initialActiveTransactionsTabRef = useRef(activeTransactionsTab);
  const hasInitializedLedgerQueryEffectRef = useRef(false);
  const fetchDataRef = useRef(null);
  const loadLedgerTransactionsRef = useRef(null);

  useEffect(() => {
    const handleViewportUpdate = () => {
      setIsMobileView(window.innerWidth < 768);
      setShowMobileAddButton(window.scrollY > 120);
    };

    handleViewportUpdate();
    window.addEventListener('resize', handleViewportUpdate);
    window.addEventListener('scroll', handleViewportUpdate, { passive: true });

    return () => {
      window.removeEventListener('resize', handleViewportUpdate);
      window.removeEventListener('scroll', handleViewportUpdate);
    };
  }, []);

  useEffect(() => {
    if (!isMobileView && activeAddMenu === 'floating') {
      setActiveAddMenu(null);
    }
  }, [isMobileView, activeAddMenu]);

  useEffect(() => {
    const handleScrollDirection = () => {
      const currentScrollY = window.scrollY;
      const isScrollingUp = currentScrollY < lastScrollYRef.current;
      setShowScrollTopButton(currentScrollY > 240 && isScrollingUp);
      setShowFloatingTransactionsHeader(isMobileView && currentScrollY > 180 && isScrollingUp);
      lastScrollYRef.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScrollDirection, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScrollDirection);
    };
  }, [isMobileView]);

  useEffect(() => {
    if (!isMobileView) {
      setShowFloatingTransactionsHeader(false);
    }
  }, [isMobileView]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!activeAddMenu) {
        return;
      }
      const clickedInsideTopMenu = topAddMenuRef.current && topAddMenuRef.current.contains(event.target);
      const clickedInsideFloatingMenu = floatingAddMenuRef.current && floatingAddMenuRef.current.contains(event.target);

      if (!clickedInsideTopMenu && !clickedInsideFloatingMenu) {
        setActiveAddMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeAddMenu]);

  useEffect(() => {
    const handleClickOutsideTransactionActions = (event) => {
      if (!activeTransactionActionId) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      if (
        target.closest('[data-transaction-action-menu]') ||
        target.closest('[data-transaction-action-trigger]') ||
        target.closest('[data-searchable-select-menu]')
      ) {
        return;
      }

      setActiveTransactionActionId('');
    };

    document.addEventListener('mousedown', handleClickOutsideTransactionActions);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideTransactionActions);
    };
  }, [activeTransactionActionId]);

  useEffect(() => {
    const handleClickOutsideSortMenu = (event) => {
      if (!showSortMenu) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      if (target.closest('[data-sort-menu]') || target.closest('[data-sort-trigger]')) {
        return;
      }

      setShowSortMenu(false);
    };

    document.addEventListener('mousedown', handleClickOutsideSortMenu);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideSortMenu);
    };
  }, [showSortMenu]);

  useEffect(() => {
    const deleteTimeouts = deleteTimeoutsRef.current;

    return () => {
      if (savedHighlightTimeoutRef.current) {
        clearTimeout(savedHighlightTimeoutRef.current);
      }
      deleteTimeouts.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      deleteTimeouts.clear();
    };
  }, []);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setSearchText(searchTextInput);
    }, 250);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTextInput]);

  useEffect(() => {
    setPersistedUiState({
      searchText: searchTextInput,
      searchAmountMin,
      searchAmountMax,
      filterDateFrom,
      filterDateTo,
      filterCategory,
      filterPaymentMethod,
      showSearchBar,
      expandedReceipts,
      showSearchFilters,
      activeSearchFilter,
      sortOrder,
      activeTransactionsTab,
      showAllItemsByReceipt,
      expandedUpcomingRecurringByTemplate,
    });
  }, [
    setPersistedUiState,
    searchTextInput,
    searchAmountMin,
    searchAmountMax,
    filterDateFrom,
    filterDateTo,
    filterCategory,
    filterPaymentMethod,
    showSearchBar,
    expandedReceipts,
    showSearchFilters,
    activeSearchFilter,
    sortOrder,
    activeTransactionsTab,
    showAllItemsByReceipt,
    expandedUpcomingRecurringByTemplate,
  ]);

  const ledgerQueryParams = useMemo(() => {
    const params = {};
    const normalizedSearchText = String(searchText || '').trim();
    const normalizedAmountMin = String(searchAmountMin || '').trim();
    const normalizedAmountMax = String(searchAmountMax || '').trim();
    const normalizedDateFrom = String(filterDateFrom || '').trim();
    const normalizedDateTo = String(filterDateTo || '').trim();
    const normalizedCategory = String(filterCategory || '').trim();
    const normalizedPaymentMethod = String(filterPaymentMethod || '').trim();

    if (normalizedSearchText) {
      params.q = normalizedSearchText;
    }
    if (normalizedAmountMin) {
      params.amountMin = normalizedAmountMin;
    }
    if (normalizedAmountMax) {
      params.amountMax = normalizedAmountMax;
    }
    if (normalizedDateFrom) {
      params.dateFrom = normalizedDateFrom;
    }
    if (normalizedDateTo) {
      params.dateTo = normalizedDateTo;
    }
    if (normalizedCategory) {
      params.categoryId = normalizedCategory;
    }
    if (normalizedPaymentMethod) {
      params.paymentMethod = normalizedPaymentMethod;
    }

    return params;
  }, [
    searchText,
    searchAmountMin,
    searchAmountMax,
    filterDateFrom,
    filterDateTo,
    filterCategory,
    filterPaymentMethod,
  ]);

  const loadUpcomingRecurringData = async (options = {}) => {
    const { forceRefresh = false } = options;

    try {
      setUpcomingRecurringLoading(true);
      setUpcomingRecurringLoadError('');
      const upcomingRecurringData = await transactionsAPI.getUpcomingRecurring(
        forceRefresh ? { forceRefresh: true } : {}
      );
      setUpcomingRecurring(upcomingRecurringData);
      setHasLoadedUpcomingRecurring(true);
      return upcomingRecurringData;
    } catch (err) {
      console.error('Error fetching upcoming recurring transactions:', err);
      setUpcomingRecurringLoadError(err?.response?.data?.error || 'Failed to load future recurring transactions');
      throw err;
    } finally {
      setUpcomingRecurringLoading(false);
    }
  };

  const loadCategoriesData = async (options = {}) => {
    const { forceRefresh = false } = options;
    const categoriesData = await categoriesAPI.getAll(forceRefresh ? { forceRefresh: true } : {});
    setCategories(categoriesData);
    return categoriesData;
  };

  const loadLedgerTransactions = async (options = {}) => {
    const {
      forceRefresh = false,
      showLoadingState = true,
    } = options;

    try {
      if (showLoadingState) {
        setLoading(true);
      }
      const transactionsData = await transactionsAPI.getAll(
        ledgerQueryParams,
        forceRefresh ? { forceRefresh: true } : {}
      );
      setTransactions(transactionsData);
      setLastLoadedTransactionCount(Array.isArray(transactionsData) ? transactionsData.length : 0);
      setLoadError('');
      return transactionsData;
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setLoadError(err?.response?.data?.error || 'Failed to load transactions');
      setError('Failed to load transactions');
      throw err;
    } finally {
      if (showLoadingState) {
        setLoading(false);
      }
    }
  };

  loadLedgerTransactionsRef.current = loadLedgerTransactions;

  const fetchData = async (options = {}) => {
    const {
      includeUpcomingRecurring = activeTransactionsTab === 'upcoming' || hasLoadedUpcomingRecurring,
      forceRefresh = false,
    } = options;

    try {
      setLoading(true);
      await Promise.all([
        loadLedgerTransactions({ forceRefresh, showLoadingState: false }),
        loadCategoriesData({ forceRefresh }),
      ]);
      if (includeUpcomingRecurring) {
        await loadUpcomingRecurringData({ forceRefresh });
      }
    } catch (err) {
      console.error('Error refreshing transaction view:', err);
    } finally {
      setLoading(false);
    }
  };

  fetchDataRef.current = fetchData;

  useEffect(() => {
    fetchDataRef.current?.({
      includeUpcomingRecurring: initialActiveTransactionsTabRef.current === 'upcoming',
    });
  }, []);

  useEffect(() => {
    if (!hasInitializedLedgerQueryEffectRef.current) {
      hasInitializedLedgerQueryEffectRef.current = true;
      return;
    }

    loadLedgerTransactionsRef.current?.().catch(() => {
      // Error state is handled in component state.
    });
  }, [ledgerQueryParams]);

  useEffect(() => {
    if (loading) {
      return;
    }
    if (activeTransactionsTab === 'upcoming' && !hasLoadedUpcomingRecurring && !upcomingRecurringLoading) {
      loadUpcomingRecurringData().catch(() => {
        // Error state is handled in component state for the future tab UI.
      });
    }
  }, [activeTransactionsTab, hasLoadedUpcomingRecurring, loading, upcomingRecurringLoading]);

  const handleStartEditRecurringTemplate = (template, applyFromDate = null) => {
    const effectiveApplyFromDate = toIsoDate(applyFromDate || template.transactionDate || template.recurrenceStartDate);
    setEditingRecurringTemplateId(String(template.recurringTransactionId || ''));
    setEditingRecurringAmount(String(Number(toNumber(template.itemTotal).toFixed(2))));
    setEditingRecurringFrequency(String(template.recurrenceFrequency || '').trim().toLowerCase());
    setEditingRecurringEndDate(toIsoDate(template.recurrenceEndDate));
    setEditingRecurringApplyFromDate(effectiveApplyFromDate);
    setEditingRecurringTransactionDate(effectiveApplyFromDate);
    setError('');
    setSuccess('');
  };

  const handleCancelEditRecurringTemplate = () => {
    setEditingRecurringTemplateId('');
    setEditingRecurringAmount('');
    setEditingRecurringFrequency('');
    setEditingRecurringEndDate('');
    setEditingRecurringApplyFromDate('');
    setEditingRecurringTransactionDate('');
    setSavingRecurringTemplateId('');
  };

  const handleSaveRecurringTemplate = async () => {
    const recurringTemplateId = String(editingRecurringTemplateId || '').trim();
    if (!recurringTemplateId) {
      return;
    }

    const parsedAmount = Number(editingRecurringAmount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setError('Recurring amount must be a valid positive number.');
      return;
    }

    const normalizedFrequency = String(editingRecurringFrequency || '').trim().toLowerCase();
    const validRecurrenceFrequency = RECURRENCE_FREQUENCY_OPTIONS.some(
      (option) => option.value === normalizedFrequency
    );
    if (!validRecurrenceFrequency) {
      setError('Select a valid recurring frequency.');
      return;
    }

    try {
      setSavingRecurringTemplateId(recurringTemplateId);
      await transactionsAPI.updateRecurringTemplate(recurringTemplateId, {
        itemTotal: Number(parsedAmount.toFixed(2)),
        recurrenceFrequency: normalizedFrequency,
        recurrenceEndDate: editingRecurringEndDate || null,
        sourceOccurrenceDate: editingRecurringApplyFromDate || null,
        newTransactionDate: editingRecurringTransactionDate || null,
      });
      await fetchData();
      setSuccess('Recurring schedule updated successfully.');
      handleCancelEditRecurringTemplate();
    } catch (err) {
      console.error('Error updating recurring template:', err);
      setError(err?.response?.data?.error || 'Failed to update recurring schedule.');
      setSavingRecurringTemplateId('');
    }
  };

  const handleDeleteRecurringSeries = async (seedTemplateId, applyFromDate) => {
    const templateId = String(seedTemplateId || '').trim();
    if (!templateId) {
      return;
    }
    const fromDate = String(applyFromDate || '').trim();
    if (!fromDate) {
      setError('Please select a valid recurring occurrence to delete.');
      return;
    }

    const confirmed = window.confirm(
      'Warning: this will delete this selected recurring occurrence and all remaining future occurrences. Do you want to continue?'
    );
    if (!confirmed) {
      return;
    }

    try {
      setSavingRecurringTemplateId(templateId);
      await transactionsAPI.deleteRecurringTemplateFuture(templateId, fromDate);
      await fetchData();
      setSuccess('Recurring occurrence and future occurrences deleted successfully.');
      if (templateId === String(editingRecurringTemplateId || '').trim()) {
        handleCancelEditRecurringTemplate();
      } else {
        setSavingRecurringTemplateId('');
      }
    } catch (err) {
      console.error('Error deleting recurring series:', err);
      setError(err?.response?.data?.error || 'Failed to delete recurring schedule.');
      setSavingRecurringTemplateId('');
    }
  };

  const paymentMethods = useMemo(() => {
    const methods = new Set(
      [...transactions, ...upcomingRecurring]
        .map((transaction) => String(transaction.paymentMethod || '').trim())
        .filter(Boolean)
    );

    if (filterPaymentMethod) {
      methods.add(String(filterPaymentMethod).trim());
    }

    return Array.from(methods).sort((a, b) => a.localeCompare(b));
  }, [transactions, upcomingRecurring, filterPaymentMethod]);

  const categoriesById = useMemo(() => {
    const map = new Map();
    categories.forEach((category) => {
      map.set(String(category.id), category);
    });
    return map;
  }, [categories]);

  const topLevelCategories = useMemo(
    () =>
      categories
        .filter((category) => !category.parentCategoryId)
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))),
    [categories]
  );

  const childCategoriesByParentId = useMemo(() => {
    const map = new Map();
    categories
      .filter((category) => category.parentCategoryId)
      .forEach((category) => {
        const parentId = String(category.parentCategoryId);
        if (!map.has(parentId)) {
          map.set(parentId, []);
        }
        map.get(parentId).push(category);
      });

    map.forEach((children, parentId) => {
      map.set(parentId, [...children].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))));
    });

    return map;
  }, [categories]);

  const categoryDisplayNameById = useMemo(() => {
    const map = new Map();
    categories.forEach((category) => {
      if (category.parentCategoryId) {
        const parent = categoriesById.get(String(category.parentCategoryId));
        if (parent) {
          map.set(String(category.id), `${parent.name} > ${category.name}`);
          return;
        }
      }
      map.set(String(category.id), category.name);
    });
    return map;
  }, [categories, categoriesById]);

  const orderedCategoryOptions = useMemo(() => {
    const options = [];
    topLevelCategories.forEach((parent) => {
      options.push(parent);
      const children = childCategoriesByParentId.get(String(parent.id)) || [];
      options.push(...children);
    });

    const addedIds = new Set(options.map((category) => String(category.id)));
    const orphanedCategories = categories
      .filter((category) => !addedIds.has(String(category.id)))
      .sort((a, b) => {
        const parentA = categoriesById.get(String(a.parentCategoryId || ''));
        const parentB = categoriesById.get(String(b.parentCategoryId || ''));
        const parentCompare = String(parentA?.name || '').localeCompare(String(parentB?.name || ''));
        if (parentCompare !== 0) {
          return parentCompare;
        }
        return String(a.name || '').localeCompare(String(b.name || ''));
      });
    options.push(...orphanedCategories);

    return options;
  }, [categories, topLevelCategories, childCategoriesByParentId, categoriesById]);

  const categorySelectOptions = useMemo(
    () => ([
      { value: '', label: 'Category *' },
      ...orderedCategoryOptions.map((category) => {
        const categoryId = String(category.id);
        if (category.parentCategoryId) {
          const parent = categoriesById.get(String(category.parentCategoryId));
          return {
            value: categoryId,
            label: category.name,
            description: parent?.name || 'Subcategory',
            indent: true,
            searchText: `${parent?.name || ''} ${category.name}`,
          };
        }
        return {
          value: categoryId,
          label: category.name,
          searchText: category.name,
        };
      }),
      { value: ADD_CATEGORY_OPTION, label: '+ Add Category', searchText: 'add category new category' },
    ]),
    [orderedCategoryOptions, categoriesById]
  );

  const categoryFilterOptions = useMemo(
    () => ([
      { value: '', label: 'All categories' },
      ...orderedCategoryOptions.map((category) => ({
        value: String(category.id),
        label: categoryDisplayNameById.get(String(category.id)) || category.name,
      })),
    ]),
    [orderedCategoryOptions, categoryDisplayNameById]
  );
  const actionCategoryDropdownOptions = categorySelectOptions;
  const historicalImportCategoryOptions = useMemo(
    () => categorySelectOptions.filter((option) => option.value && option.value !== ADD_CATEGORY_OPTION),
    [categorySelectOptions]
  );

  const createCategoryNameSuggestions = useMemo(() => {
    const query = toComparableText(newCategoryForm.name);
    return topLevelCategories
      .filter((category) => !query || toComparableText(category.name).includes(query))
      .slice(0, 6);
  }, [topLevelCategories, newCategoryForm.name]);
  const matchedMainCategoryForCreate = useMemo(() => {
    const selectedParentCategoryId = String(newCategoryForm.parentCategoryId || '').trim();
    if (selectedParentCategoryId) {
      return null;
    }
    const query = toComparableText(newCategoryForm.name);
    if (!query) {
      return null;
    }
    return topLevelCategories.find((category) => toComparableText(category.name) === query) || null;
  }, [topLevelCategories, newCategoryForm.name, newCategoryForm.parentCategoryId]);
  const existingCreateSubcategories = useMemo(
    () => (matchedMainCategoryForCreate ? (childCategoriesByParentId.get(String(matchedMainCategoryForCreate.id)) || []) : []),
    [matchedMainCategoryForCreate, childCategoriesByParentId]
  );
  const visibleCreateSubcategoryRefs = useMemo(
    () => (showAllCreateSubcategoryRefs ? existingCreateSubcategories : existingCreateSubcategories.slice(0, 5)),
    [showAllCreateSubcategoryRefs, existingCreateSubcategories]
  );
  const hiddenCreateSubcategoryRefCount = Math.max(existingCreateSubcategories.length - visibleCreateSubcategoryRefs.length, 0);
  const activeActionTransaction = useMemo(() => {
    if (!activeTransactionActionId) {
      return null;
    }

    return transactions.find(
      (transaction) => String(transaction.id) === String(activeTransactionActionId)
    ) || null;
  }, [transactions, activeTransactionActionId]);
  const quickCategoryIds = useMemo(() => {
    const sortedTransactions = [...transactions].sort(
      (a, b) =>
        parseTransactionDate(b.transactionDate).getTime() -
        parseTransactionDate(a.transactionDate).getTime()
    );
    const seen = new Set();
    const topIds = [];

    sortedTransactions.forEach((transaction) => {
      const categoryId = String(transaction.categoryId || '');
      if (!categoryId || seen.has(categoryId) || !categoryDisplayNameById.has(categoryId)) {
        return;
      }
      seen.add(categoryId);
      topIds.push(categoryId);
    });

    return topIds.slice(0, 3);
  }, [transactions, categoryDisplayNameById]);

  const applyFiltersToUpcomingRecurring = useCallback((collection) => {
    const textQuery = toComparableText(searchText);
    const amountMin = searchAmountMin === '' ? null : toNumber(searchAmountMin);
    const amountMax = searchAmountMax === '' ? null : toNumber(searchAmountMax);
    const fromDate = filterDateFrom ? new Date(`${filterDateFrom}T00:00:00`) : null;
    const toDate = filterDateTo ? new Date(`${filterDateTo}T23:59:59`) : null;

    return collection.filter((transaction) => {
      const transactionAmount = toNumber(transaction.itemTotal);
      const transactionDate = parseTransactionDate(transaction.transactionDate);
      const transactionCategory = String(transaction.categoryId || '');
      const transactionPaymentMethod = String(transaction.paymentMethod || '').trim();
      const categoryName = categoryDisplayNameById.get(transactionCategory) || '';
      const searchableText = toComparableText(
        [
          transaction.storeName,
          transaction.itemName,
          categoryName,
          transaction.paymentMethod,
          toIsoDate(transaction.transactionDate),
          transaction.itemTotal,
        ].join(' ')
      );

      if (textQuery && !searchableText.includes(textQuery)) {
        return false;
      }

      if (amountMin !== null && transactionAmount < amountMin) {
        return false;
      }

      if (amountMax !== null && transactionAmount > amountMax) {
        return false;
      }

      if (fromDate && transactionDate < fromDate) {
        return false;
      }

      if (toDate && transactionDate > toDate) {
        return false;
      }

      if (filterCategory && transactionCategory !== filterCategory) {
        return false;
      }

      if (filterPaymentMethod && transactionPaymentMethod !== filterPaymentMethod) {
        return false;
      }

      return true;
    });
  }, [
    searchText,
    searchAmountMin,
    searchAmountMax,
    filterDateFrom,
    filterDateTo,
    filterCategory,
    filterPaymentMethod,
    categoryDisplayNameById,
  ]);

  const filteredTransactions = useMemo(() => {
    return transactions;
  }, [transactions]);

  const filteredUpcomingRecurring = useMemo(() => {
    return applyFiltersToUpcomingRecurring(upcomingRecurring);
  }, [
    upcomingRecurring,
    applyFiltersToUpcomingRecurring,
  ]);
  const upcomingRecurringGroups = useMemo(() => {
    const compareUpcomingRecurringTransactions = (a, b) => {
      const dateDifference =
        parseTransactionDate(a?.transactionDate).getTime() - parseTransactionDate(b?.transactionDate).getTime();
      const amountDifference = toNumber(a?.itemTotal) - toNumber(b?.itemTotal);

      if (sortOrder === 'oldest') {
        return dateDifference || amountDifference;
      }
      if (sortOrder === 'lowest') {
        return amountDifference || dateDifference;
      }
      if (sortOrder === 'highest') {
        return (amountDifference * -1) || dateDifference;
      }
      return (dateDifference * -1) || amountDifference;
    };

    const bySeriesKey = new Map();
    filteredUpcomingRecurring.forEach((transaction) => {
      const templateId = String(transaction.recurringTransactionId || '').trim();
      if (!templateId) {
        return;
      }
      const seriesKey = [
        String(transaction.storeName || '').trim().toLowerCase(),
        String(transaction.itemName || '').trim().toLowerCase(),
        String(transaction.categoryId || '').trim().toLowerCase(),
        String(transaction.paymentMethod || '').trim().toLowerCase(),
      ].join('|');

      if (!bySeriesKey.has(seriesKey)) {
        bySeriesKey.set(seriesKey, {
          groupKey: seriesKey,
          headerOccurrence: transaction,
          chronologicalOccurrences: [],
          occurrences: [],
          totalAmount: 0,
          recurringTransactionIds: new Set(),
        });
      }

      const group = bySeriesKey.get(seriesKey);
      group.chronologicalOccurrences.push(transaction);
      group.totalAmount += toNumber(transaction.itemTotal);
      group.recurringTransactionIds.add(templateId);
    });

    const groups = Array.from(bySeriesKey.values());
    groups.forEach((group) => {
      group.chronologicalOccurrences.sort(
        (a, b) => parseTransactionDate(a.transactionDate).getTime() - parseTransactionDate(b.transactionDate).getTime()
      );
      group.headerOccurrence = group.chronologicalOccurrences[0] || group.headerOccurrence;
      group.occurrences = [...group.chronologicalOccurrences].sort(compareUpcomingRecurringTransactions);
      group.recurringTransactionIds = Array.from(group.recurringTransactionIds);
    });

    return groups.sort((a, b) => compareUpcomingRecurringTransactions(a.headerOccurrence, b.headerOccurrence));
  }, [filteredUpcomingRecurring, sortOrder]);
  const currentEditingRecurringTemplate = useMemo(() => {
    if (!editingRecurringTemplateId) {
      return null;
    }

    for (const group of upcomingRecurringGroups) {
      const match = (group.occurrences || []).find(
        (occurrence) => String(occurrence.recurringTransactionId || '') === String(editingRecurringTemplateId)
      );
      if (match) {
        return match;
      }
    }
    return null;
  }, [editingRecurringTemplateId, upcomingRecurringGroups]);
  const getGroupRecurrenceRange = (group) => {
    const starts = group.occurrences
      .map((entry) => parseTransactionDate(entry.recurrenceStartDate))
      .filter((date) => !Number.isNaN(date.getTime()))
      .sort((a, b) => a.getTime() - b.getTime());
    const hasOpenEnded = group.occurrences.some((entry) => !toIsoDate(entry.recurrenceEndDate));
    const ends = hasOpenEnded
      ? []
      : group.occurrences
          .map((entry) => parseTransactionDate(entry.recurrenceEndDate))
          .filter((date) => !Number.isNaN(date.getTime()))
          .sort((a, b) => b.getTime() - a.getTime());

    const start = starts.length ? starts[0] : null;
    const end = hasOpenEnded ? null : (ends.length ? ends[0] : null);
    return formatRecurrenceRange(start, end);
  };

  const groupedReceipts = useMemo(() => {
    const grouped = groupTransactionsByReceipt(filteredTransactions);
    return [...grouped].sort((a, b) => {
      if (sortOrder === 'oldest') {
        return parseTransactionDate(a.transactionDate).getTime() - parseTransactionDate(b.transactionDate).getTime();
      }
      if (sortOrder === 'lowest') {
        const amountA = Number(a.totalAmount || 0) + (a.hasTaxLineItem ? 0 : Number(a.taxAmount || 0));
        const amountB = Number(b.totalAmount || 0) + (b.hasTaxLineItem ? 0 : Number(b.taxAmount || 0));
        return amountA - amountB;
      }
      if (sortOrder === 'highest') {
        const amountA = Number(a.totalAmount || 0) + (a.hasTaxLineItem ? 0 : Number(a.taxAmount || 0));
        const amountB = Number(b.totalAmount || 0) + (b.hasTaxLineItem ? 0 : Number(b.taxAmount || 0));
        return amountB - amountA;
      }
      return parseTransactionDate(b.transactionDate).getTime() - parseTransactionDate(a.transactionDate).getTime();
    });
  }, [filteredTransactions, sortOrder]);
  const filtersAreActive = useMemo(
    () =>
      hasActiveFilters({
        searchText: searchTextInput,
        searchAmountMin,
        searchAmountMax,
        filterDateFrom,
        filterDateTo,
        filterCategory,
        filterPaymentMethod,
      }),
    [
      searchTextInput,
      searchAmountMin,
      searchAmountMax,
      filterDateFrom,
      filterDateTo,
      filterCategory,
      filterPaymentMethod,
    ]
  );
  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (searchTextInput.trim()) {
      chips.push({ key: 'search', label: `Search: ${searchTextInput.trim()}` });
    }
    if (searchAmountMin !== '') {
      chips.push({ key: 'amountMin', label: `Min: ${searchAmountMin}` });
    }
    if (searchAmountMax !== '') {
      chips.push({ key: 'amountMax', label: `Max: ${searchAmountMax}` });
    }
    if (filterDateFrom) {
      chips.push({ key: 'dateFrom', label: `From: ${filterDateFrom}` });
    }
    if (filterDateTo) {
      chips.push({ key: 'dateTo', label: `To: ${filterDateTo}` });
    }
    if (filterCategory) {
      chips.push({ key: 'category', label: `Category: ${categoryDisplayNameById.get(String(filterCategory)) || 'Unknown'}` });
    }
    if (filterPaymentMethod) {
      chips.push({ key: 'payment', label: `Payment: ${filterPaymentMethod}` });
    }
    return chips;
  }, [
    searchTextInput,
    searchAmountMin,
    searchAmountMax,
    filterDateFrom,
    filterDateTo,
    filterCategory,
    filterPaymentMethod,
    categoryDisplayNameById,
  ]);
  const manualItemsSubtotal = useMemo(
    () => manualForm.items.reduce((sum, item) => sum + toNumber(resolveManualItemTotal(item)), 0),
    [manualForm.items]
  );
  const manualTaxAmount = useMemo(
    () => {
      const parsed = Number(manualForm.taxAmount);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    },
    [manualForm.taxAmount]
  );
  const manualGrandTotal = useMemo(
    () => Number((manualItemsSubtotal + manualTaxAmount).toFixed(2)),
    [manualItemsSubtotal, manualTaxAmount]
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = parseTransactionDate(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const clearAllFilters = () => {
    setSearchTextInput('');
    setSearchText('');
    setSearchAmountMin('');
    setSearchAmountMax('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterCategory('');
    setFilterPaymentMethod('');
    setShowSearchBar(false);
    setExpandedReceipts({});
    setActiveSearchFilter('amount');
    setShowSearchFilters(false);
  };

  const clearSingleFilter = (filterKey) => {
    switch (filterKey) {
      case 'search':
        setSearchTextInput('');
        setSearchText('');
        break;
      case 'amountMin':
        setSearchAmountMin('');
        break;
      case 'amountMax':
        setSearchAmountMax('');
        break;
      case 'dateFrom':
        setFilterDateFrom('');
        break;
      case 'dateTo':
        setFilterDateTo('');
        break;
      case 'category':
        setFilterCategory('');
        break;
      case 'payment':
        setFilterPaymentMethod('');
        break;
      default:
        break;
    }
  };

  const resetManualForm = () => {
    setManualForm({
      transactionDate: getTodayIsoDate(),
      storeName: '',
      storeLocation: '',
      taxAmount: '',
      paymentMethod: '',
      isRecurring: false,
      recurrenceFrequency: '',
      recurrenceStartDate: '',
      recurrenceEndDate: '',
      items: [createManualItem()],
    });
    setManualFormError('');
    setManualFieldErrors(createEmptyManualFieldErrors());
    setIsManualDetailsOpen(false);
  };

  const handleManualFormChange = (field, value) => {
    setManualForm((current) => ({
      ...current,
      [field]: value,
    }));
    setManualFieldErrors((current) => ({
      ...current,
      [field]: '',
    }));
  };

  const handleManualItemChange = (index, field, value) => {
    setManualForm((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) => (
        itemIndex === index
          ? (() => {
              const nextItem = { ...item, [field]: value };
              if (field === 'itemTotal') {
                nextItem.itemTotalOverridden = true;
              } else if (field === 'quantity' || field === 'unitPrice') {
                const recalculatedTotal = getAutoCalculatedItemTotal(nextItem);
                nextItem.itemTotalOverridden = false;
                nextItem.itemTotal = recalculatedTotal !== null ? String(recalculatedTotal) : '';
              }
              return nextItem;
            })()
          : item
      )),
    }));
    setManualFieldErrors((current) => {
      const nextItems = { ...(current.items || {}) };
      if (nextItems[index]) {
        nextItems[index] = { ...nextItems[index], [field]: '' };
      }
      return {
        ...current,
        items: nextItems,
      };
    });
  };

  const addManualItem = () => {
    setManualForm((current) => ({
      ...current,
      items: [...current.items, createManualItem()],
    }));
  };

  const removeManualItem = (index) => {
    setManualForm((current) => ({
      ...current,
      items: current.items.filter((_, itemIndex) => itemIndex !== index),
    }));
    setManualFieldErrors((current) => {
      const existingItems = current.items || {};
      const nextItems = {};
      Object.keys(existingItems).forEach((key) => {
        const numericKey = Number(key);
        if (!Number.isInteger(numericKey) || numericKey === index) {
          return;
        }
        const nextKey = numericKey > index ? numericKey - 1 : numericKey;
        nextItems[nextKey] = existingItems[key];
      });
      return {
        ...current,
        items: nextItems,
      };
    });
  };

  const closeManualModal = () => {
    setShowManualModal(false);
    setShowManualDuplicateConfirm(false);
    setManualModalTitle('Add Transaction');
    resetManualForm();
  };

  const closeHistoricalImportModal = () => {
    setShowHistoricalImportModal(false);
    setImportingHistorical(false);
  };

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && showManualModal) {
        closeManualModal();
        return;
      }

      if (event.key === 'Escape' && showHistoricalImportModal) {
        closeHistoricalImportModal();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showManualModal, showHistoricalImportModal]);

  const openManualModal = () => {
    setActiveAddMenu(null);
    setSuccess('');
    setError('');
    setManualModalTitle('Add Transaction');
    setManualFieldErrors(createEmptyManualFieldErrors());
    if (isMobileView) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsManualDetailsOpen(false);
    setShowManualModal(true);
  };

  const openCopyTransactionModal = ({
    transactionDate,
    storeName,
    storeLocation,
    taxAmount,
    paymentMethod,
    items,
  }) => {
    if (!Array.isArray(items) || items.length === 0) {
      return;
    }

    setActiveAddMenu(null);
    setSuccess('');
    setError('');
    setShowManualDuplicateConfirm(false);
    setManualModalTitle('Copy Transaction');
    setManualFieldErrors(createEmptyManualFieldErrors());
    setManualFormError('');
    setManualForm({
      transactionDate: toIsoDate(transactionDate) || getTodayIsoDate(),
      storeName: String(storeName || '').trim(),
      storeLocation: String(storeLocation || '').trim(),
      taxAmount:
        taxAmount == null || taxAmount === ''
          ? ''
          : String(Number(toNumber(taxAmount).toFixed(2))),
      paymentMethod: String(paymentMethod || '').trim(),
      isRecurring: false,
      recurrenceFrequency: '',
      recurrenceStartDate: '',
      recurrenceEndDate: '',
      items: items.map((item) => ({
        itemName: String(item.itemName || '').trim(),
        itemTotal: String(Number(toNumber(item.itemTotal).toFixed(2))),
        categoryId: String(item.categoryId || '').trim(),
        quantity:
          item.quantity == null || item.quantity === ''
            ? ''
            : String(item.quantity),
        unitPrice:
          item.unitPrice == null || item.unitPrice === ''
            ? ''
            : String(Number(toNumber(item.unitPrice).toFixed(2))),
        itemTotalOverridden: false,
      })),
    });
    setIsManualDetailsOpen(true);
    if (isMobileView) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setShowManualModal(true);
  };

  const handleCopyReceipt = (receipt) => {
    if (!receipt || !Array.isArray(receipt.items) || receipt.items.length === 0) {
      return;
    }

    openCopyTransactionModal({
      transactionDate: receipt.transactionDate,
      storeName: receipt.storeName,
      storeLocation: receipt.storeLocation,
      taxAmount: receipt.hasTaxLineItem ? 0 : receipt.taxAmount,
      paymentMethod: receipt.paymentMethod,
      items: receipt.items.map((item) => ({
        itemName: item.itemName,
        itemTotal: item.itemTotal,
        categoryId: item.categoryId,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
      })),
    });
  };

  const openHistoricalImportModal = () => {
    setActiveAddMenu(null);
    setSuccess('');
    setError('');
    if (isMobileView) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setShowHistoricalImportModal(true);
  };

  const triggerHistoricalImportFilePicker = () => {
    openHistoricalImportModal();
    if (historicalImportInputRef.current) {
      historicalImportInputRef.current.click();
    }
  };

  useEffect(() => {
    if (showManualModal && manualModalRef.current) {
      manualModalRef.current.scrollTop = 0;
    }
  }, [showManualModal]);

  useEffect(() => {
    if (showHistoricalImportModal && historicalImportModalRef.current) {
      historicalImportModalRef.current.scrollTop = 0;
    }
  }, [showHistoricalImportModal]);

  const toggleTopAddMenu = () => {
    setActiveAddMenu((current) => (current === 'top' ? null : 'top'));
  };

  const toggleFloatingAddMenu = () => {
    setActiveAddMenu((current) => (current === 'floating' ? null : 'floating'));
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const manualTransactionDate = manualForm.transactionDate
    ? new Date(`${manualForm.transactionDate}T00:00:00`)
    : null;
  const filterDateFromValue = filterDateFrom ? new Date(`${filterDateFrom}T00:00:00`) : null;
  const filterDateToValue = filterDateTo ? new Date(`${filterDateTo}T00:00:00`) : null;
  const editingRecurringTransactionDateValue = editingRecurringTransactionDate
    ? new Date(`${editingRecurringTransactionDate}T00:00:00`)
    : null;
  const editingRecurringEndDateValue = editingRecurringEndDate
    ? new Date(`${editingRecurringEndDate}T00:00:00`)
    : null;
  const editingReceiptTransactionDateValue = editingReceiptDraft.transactionDate
    ? new Date(`${editingReceiptDraft.transactionDate}T00:00:00`)
    : null;
  const quickManualItem = manualForm.items?.[0] || createManualItem();
  const manualRecurrenceStartDate = manualForm.recurrenceStartDate
    ? new Date(`${manualForm.recurrenceStartDate}T00:00:00`)
    : null;
  const manualRecurrenceEndDate = manualForm.recurrenceEndDate
    ? new Date(`${manualForm.recurrenceEndDate}T00:00:00`)
    : null;

  const handleManualDateChange = (date) => {
    if (!date) {
      handleManualFormChange('transactionDate', '');
      return;
    }

    const timezoneOffsetMs = date.getTimezoneOffset() * 60000;
    const localIsoDate = new Date(date.getTime() - timezoneOffsetMs).toISOString().split('T')[0];
    setManualForm((current) => {
      const shouldSyncRecurrenceStart =
        current.isRecurring &&
        (!current.recurrenceStartDate || current.recurrenceStartDate === current.transactionDate);

      return {
        ...current,
        transactionDate: localIsoDate,
        recurrenceStartDate: shouldSyncRecurrenceStart ? localIsoDate : current.recurrenceStartDate,
      };
    });
    setManualFieldErrors((current) => ({
      ...current,
      transactionDate: '',
    }));
  };

  const handleManualRecurrenceStartDateChange = (date) => {
    if (!date) {
      handleManualFormChange('recurrenceStartDate', '');
      return;
    }

    const timezoneOffsetMs = date.getTimezoneOffset() * 60000;
    const localIsoDate = new Date(date.getTime() - timezoneOffsetMs).toISOString().split('T')[0];

    setManualForm((current) => ({
      ...current,
      recurrenceStartDate: localIsoDate,
      recurrenceEndDate:
        current.recurrenceEndDate && current.recurrenceEndDate < localIsoDate
          ? ''
          : current.recurrenceEndDate,
    }));
  };

  const handleManualRecurrenceEndDateChange = (date) => {
    if (!date) {
      handleManualFormChange('recurrenceEndDate', '');
      return;
    }

    const timezoneOffsetMs = date.getTimezoneOffset() * 60000;
    const localIsoDate = new Date(date.getTime() - timezoneOffsetMs).toISOString().split('T')[0];
    handleManualFormChange('recurrenceEndDate', localIsoDate);
  };

  const handleManualRecurringToggle = (enabled) => {
    setManualForm((current) => {
      if (!enabled) {
        return {
          ...current,
          isRecurring: false,
          recurrenceFrequency: 'monthly',
          recurrenceStartDate: '',
          recurrenceEndDate: '',
        };
      }

      return {
        ...current,
        isRecurring: true,
        recurrenceFrequency: current.recurrenceFrequency || '',
        recurrenceStartDate: current.recurrenceStartDate || current.transactionDate,
      };
    });
  };

  const handleQuickManualItemChange = (field, value) => {
    setManualForm((current) => {
      const nextItems = Array.isArray(current.items) && current.items.length > 0
        ? [...current.items]
        : [createManualItem()];
      const nextItem = {
        ...nextItems[0],
        [field]: value,
      };
      if (field === 'itemTotal') {
        nextItem.itemTotalOverridden = true;
      } else if (field === 'quantity' || field === 'unitPrice') {
        const recalculatedTotal = getAutoCalculatedItemTotal(nextItem);
        nextItem.itemTotalOverridden = false;
        nextItem.itemTotal = recalculatedTotal !== null ? String(recalculatedTotal) : '';
      }
      nextItems[0] = nextItem;
      return {
        ...current,
        items: nextItems,
      };
    });
    setManualFieldErrors((current) => {
      const nextItems = { ...(current.items || {}) };
      if (nextItems[0]) {
        nextItems[0] = {
          ...nextItems[0],
          [field]: '',
        };
      }
      return {
        ...current,
        items: nextItems,
      };
    });
  };

  const manualRecurrencePreview = useMemo(() => {
    if (!manualForm.isRecurring || !manualForm.recurrenceFrequency || !manualForm.recurrenceStartDate) {
      return '';
    }

    const frequencyLabel = RECURRENCE_FREQUENCY_LABELS[manualForm.recurrenceFrequency];
    const formattedStart = formatRecurrencePreviewDate(manualForm.recurrenceStartDate);
    if (!frequencyLabel || !formattedStart) {
      return '';
    }

    const formattedEnd = manualForm.recurrenceEndDate
      ? formatRecurrencePreviewDate(manualForm.recurrenceEndDate)
      : '';
    if (formattedEnd) {
      return `Repeats ${frequencyLabel.toLowerCase()} from ${formattedStart} until ${formattedEnd}.`;
    }
    return `Repeats ${frequencyLabel.toLowerCase()} starting ${formattedStart}.`;
  }, [
    manualForm.isRecurring,
    manualForm.recurrenceEndDate,
    manualForm.recurrenceFrequency,
    manualForm.recurrenceStartDate,
  ]);

  const saveManualTransaction = async (forceDuplicateSave = false) => {
    setManualFormError('');
    setError('');
    setSuccess('');
    const nextFieldErrors = createEmptyManualFieldErrors();

    const missingHeaderFields = [];
    if (!manualForm.transactionDate) {
      missingHeaderFields.push('transaction date');
      nextFieldErrors.transactionDate = 'Transaction date is required.';
    }
    if (!manualForm.storeName.trim()) {
      missingHeaderFields.push('merchant');
      nextFieldErrors.storeName = 'Store name is required.';
    }
    if (!manualForm.paymentMethod) {
      missingHeaderFields.push('payment method');
      nextFieldErrors.paymentMethod = 'Payment method is required.';
    }
    if (missingHeaderFields.length > 0) {
      setManualFieldErrors(nextFieldErrors);
      setManualFormError(
        `Missing required ${missingHeaderFields.length > 1 ? 'fields' : 'field'}: ${missingHeaderFields.join(', ')}.`
      );
      return;
    }

    if (!Array.isArray(manualForm.items) || manualForm.items.length === 0) {
      setManualFormError('Add at least one item.');
      return;
    }

    const normalizedItems = [];
    for (let index = 0; index < manualForm.items.length; index += 1) {
      const item = manualForm.items[index];
      const fallbackQuickItemName = index === 0 && normalizedItems.length === 0
        ? String(manualForm.storeName || '').trim()
        : '';
      const itemName = String(item.itemName || '').trim() || fallbackQuickItemName;
      const categoryId = String(item.categoryId || '').trim();
      const itemTotal = resolveManualItemTotal(item);
      const quantity = item.quantity === '' ? 1 : Number(item.quantity);
      const unitPrice = item.unitPrice === '' ? undefined : Number(item.unitPrice);

      const missingItemFields = [];
      if (!itemName) {
        missingItemFields.push('item name');
        nextFieldErrors.items[index] = {
          ...(nextFieldErrors.items[index] || {}),
          itemName: 'Item name is required.',
        };
      }
      if (!categoryId) {
        missingItemFields.push('category');
        nextFieldErrors.items[index] = {
          ...(nextFieldErrors.items[index] || {}),
          categoryId: 'Category is required.',
        };
      }
      if (!Number.isFinite(itemTotal) || itemTotal <= 0) {
        missingItemFields.push('amount');
        nextFieldErrors.items[index] = {
          ...(nextFieldErrors.items[index] || {}),
          itemTotal: 'Enter a valid item total.',
        };
      }
      if (missingItemFields.length > 0) {
        setManualFieldErrors(nextFieldErrors);
        setManualFormError(`Transaction item ${index + 1} is missing: ${missingItemFields.join(', ')}.`);
        return;
      }

      if (!Number.isFinite(quantity) || quantity <= 0) {
        nextFieldErrors.items[index] = {
          ...(nextFieldErrors.items[index] || {}),
          quantity: 'Quantity must be greater than 0.',
        };
        setManualFieldErrors(nextFieldErrors);
        setManualFormError(`Item ${index + 1}: quantity must be greater than 0.`);
        return;
      }

      if (unitPrice !== undefined && (!Number.isFinite(unitPrice) || unitPrice < 0)) {
        nextFieldErrors.items[index] = {
          ...(nextFieldErrors.items[index] || {}),
          unitPrice: 'Unit price cannot be negative.',
        };
        setManualFieldErrors(nextFieldErrors);
        setManualFormError(`Item ${index + 1}: unit price cannot be negative.`);
        return;
      }

      normalizedItems.push({
        itemName,
        categoryId,
        itemTotal,
        quantity,
        unitPrice,
      });
    }

    const taxAmount = manualForm.taxAmount === '' ? undefined : Number(manualForm.taxAmount);
    if (taxAmount !== undefined && (!Number.isFinite(taxAmount) || taxAmount < 0)) {
      nextFieldErrors.taxAmount = 'Tax amount cannot be negative.';
      setManualFieldErrors(nextFieldErrors);
      setManualFormError('Tax amount cannot be negative.');
      return;
    }

    const normalizedRecurrenceFrequency = String(manualForm.recurrenceFrequency || '').trim().toLowerCase();
    if (manualForm.isRecurring) {
      const validRecurrenceFrequency = RECURRENCE_FREQUENCY_OPTIONS.some(
        (option) => option.value === normalizedRecurrenceFrequency
      );
      if (!validRecurrenceFrequency) {
        setManualFormError('Select a valid recurring frequency.');
        return;
      }
      if (!manualForm.recurrenceStartDate) {
        setManualFormError('Recurring transactions require a start date.');
        return;
      }
      if (
        manualForm.recurrenceEndDate &&
        manualForm.recurrenceEndDate < manualForm.recurrenceStartDate
      ) {
        setManualFormError('Recurring end date must be on or after the start date.');
        return;
      }
    }
    setManualFieldErrors(createEmptyManualFieldErrors());

    try {
      setCreatingTransaction(true);
      const createdTransactions = [];
      for (let index = 0; index < normalizedItems.length; index += 1) {
        const item = normalizedItems[index];
        const created = await transactionsAPI.create({
          transactionDate: manualForm.transactionDate,
          storeName: manualForm.storeName.trim(),
          storeLocation: manualForm.storeLocation.trim() || undefined,
          paymentMethod: manualForm.paymentMethod,
          taxAmount: index === 0 ? taxAmount : undefined,
          itemName: item.itemName,
          itemTotal: item.itemTotal,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          categoryId: item.categoryId,
          receiptTotal: manualGrandTotal,
          receiptTaxAmount: toNumber(taxAmount),
          checkDuplicate: index === 0,
          forceDuplicateSave: forceDuplicateSave && index === 0,
          isRecurring: manualForm.isRecurring,
          recurrenceFrequency: manualForm.isRecurring ? normalizedRecurrenceFrequency : undefined,
          recurrenceStartDate: manualForm.isRecurring ? manualForm.recurrenceStartDate : undefined,
          recurrenceEndDate:
            manualForm.isRecurring && manualForm.recurrenceEndDate
              ? manualForm.recurrenceEndDate
              : undefined,
        });
        const materialized = Array.isArray(created?.transactions)
          ? created.transactions
          : (created?.transaction ? [created.transaction] : []);
        createdTransactions.push(...materialized);
      }

      setTransactions((current) => {
        const existingIds = new Set(current.map((transaction) => String(transaction.id)));
        const uniqueCreated = createdTransactions.filter(
          (transaction) => transaction?.id && !existingIds.has(String(transaction.id))
        );
        return [...uniqueCreated, ...current];
      });
      const refreshedUpcomingRecurring = await transactionsAPI.getUpcomingRecurring();
      setUpcomingRecurring(refreshedUpcomingRecurring);
      closeManualModal();
      if (manualForm.isRecurring) {
        setSuccess('Recurring transaction schedule saved successfully.');
      } else {
        setSuccess(`${createdTransactions.length} transaction item(s) created successfully.`);
      }
    } catch (err) {
      console.error('Error creating manual transaction:', err);
      if (
        err?.response?.status === 409 &&
        err?.response?.data?.duplicateDetected &&
        !forceDuplicateSave
      ) {
        setShowManualDuplicateConfirm(true);
        return;
      }
      setManualFormError(err.response?.data?.error || 'Failed to create transaction');
    } finally {
      setCreatingTransaction(false);
    }
  };

  const submitManualTransaction = async (event) => {
    event.preventDefault();
    await saveManualTransaction(false);
  };

  const handleHistoricalImportFileSelected = async (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      const text = await file.text();
      const preview = buildHistoricalImportPreview({ text, categories });
      setHistoricalImportFileName(file.name);
      setHistoricalImportPreview(preview);
      setHistoricalImportFallbackCategoryId('');
      setShowHistoricalImportModal(true);
    } catch (err) {
      console.error('Failed to parse historical import file:', err);
      setHistoricalImportPreview(null);
      setHistoricalImportFileName(file.name || '');
      setError(err?.message || 'Failed to parse import file.');
      setShowHistoricalImportModal(true);
    } finally {
      event.target.value = '';
    }
  };

  const handleImportHistoricalTransactions = async () => {
    if (!historicalImportPreview?.rows?.length) {
      setError('Choose a spreadsheet export before importing.');
      return;
    }

    const previewHasRowErrors = historicalImportPreview.rows.some((row) => row.errors.length > 0);
    if (previewHasRowErrors) {
      setError('Fix the spreadsheet row errors before importing.');
      return;
    }

    const { transactions: importTransactions, errors: payloadErrors } = buildHistoricalImportPayload(
      historicalImportPreview.rows,
      historicalImportFallbackCategoryId
    );

    if (payloadErrors.length > 0) {
      setError(payloadErrors[0]);
      return;
    }

    try {
      setImportingHistorical(true);
      const result = await transactionsAPI.importHistorical({
        transactions: importTransactions,
      });
      await fetchData();
      closeHistoricalImportModal();
      setHistoricalImportPreview(null);
      setHistoricalImportFileName('');
      setHistoricalImportFallbackCategoryId('');
      setSuccess(result?.message || `Imported ${importTransactions.length} historical transactions.`);
    } catch (err) {
      console.error('Failed to import historical transactions:', err);
      setError(err?.response?.data?.error || 'Failed to import historical transactions.');
    } finally {
      setImportingHistorical(false);
    }
  };

  const toggleReceipt = (receiptKey) => {
    setShowSearchBar(false);
    setShowSearchFilters(false);
    setShowSortMenu(false);
    setActiveTransactionActionId('');
    setExpandedReceipts((current) => ({
      ...groupedReceipts.reduce((accumulator, receipt) => {
        accumulator[receipt.key] = false;
        return accumulator;
      }, {}),
      [receiptKey]: !(
        Object.prototype.hasOwnProperty.call(current, receiptKey)
          ? current[receiptKey]
          : filtersAreActive
      ),
    }));
    if (editingReceiptKey && String(editingReceiptKey) !== String(receiptKey)) {
      cancelEditingReceipt();
    }
  };

  const cancelEditingReceipt = () => {
    setEditingReceiptKey('');
    setEditingReceiptStep(1);
    setEditingReceiptDraft({
      transactionDate: '',
      storeName: '',
      storeLocation: '',
      paymentMethod: '',
      items: [],
    });
  };

  const handleEditingReceiptDraftChange = (field, value) => {
    setEditingReceiptDraft((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleEditingReceiptItemChange = (itemId, field, value) => {
    setEditingReceiptDraft((current) => ({
      ...current,
      items: (current.items || []).map((item) => (
        String(item.id) === String(itemId)
          ? { ...item, [field]: value }
          : item
      )),
    }));
  };

  const saveEditedReceipt = async () => {
    const nextDate = String(editingReceiptDraft.transactionDate || '').trim();
    const nextStoreName = String(editingReceiptDraft.storeName || '').trim();
    const nextPaymentMethod = String(editingReceiptDraft.paymentMethod || '').trim();
    const nextStoreLocation = String(editingReceiptDraft.storeLocation || '').trim();

    if (!nextDate || !nextStoreName || !nextPaymentMethod) {
      setError('Receipt requires date, store name, and payment type.');
      return;
    }

    if (!Array.isArray(editingReceiptDraft.items) || editingReceiptDraft.items.length === 0) {
      setError('Receipt must include at least one item.');
      return;
    }

    const normalizedItems = [];
    for (let index = 0; index < editingReceiptDraft.items.length; index += 1) {
      const item = editingReceiptDraft.items[index];
      const itemName = String(item.itemName || '').trim();
      const quantity = Number(item.quantity);
      const itemTotal = Number(item.itemTotal);
      const categoryId = String(item.categoryId || '').trim();

      const missingItemFields = [];
      if (!itemName) {
        missingItemFields.push('item name');
      }
      if (!categoryId) {
        missingItemFields.push('category');
      }
      if (!Number.isFinite(quantity) || quantity <= 0) {
        missingItemFields.push('quantity');
      }
      if (!Number.isFinite(itemTotal) || itemTotal === 0) {
        missingItemFields.push('amount');
      }
      if (missingItemFields.length > 0) {
        setError(`Item ${index + 1} is missing: ${missingItemFields.join(', ')}.`);
        return;
      }

      normalizedItems.push({
        id: String(item.id),
        itemName,
        quantity: Math.max(1, Math.round(quantity)),
        itemTotal: Number(itemTotal.toFixed(2)),
        categoryId,
      });
    }

    try {
      setSavingReceiptDraft(true);
      setError('');
      const updates = await Promise.all(
        normalizedItems.map((item) =>
          transactionsAPI.update(item.id, {
            transactionDate: nextDate,
            storeName: nextStoreName,
            storeLocation: nextStoreLocation || null,
            paymentMethod: nextPaymentMethod,
            itemName: item.itemName,
            quantity: item.quantity,
            itemTotal: item.itemTotal,
            categoryId: item.categoryId,
          })
        )
      );
      const updatesById = new Map(updates.map((transaction) => [String(transaction.id), transaction]));

      setTransactions((current) =>
        current.map((transaction) => updatesById.get(String(transaction.id)) || transaction)
      );
      cancelEditingReceipt();
      setSuccess('Receipt updated.');
    } catch (err) {
      console.error('Error updating receipt:', err);
      setError(err.response?.data?.error || 'Failed to update receipt');
    } finally {
      setSavingReceiptDraft(false);
    }
  };

  const handleCategoryChange = async (transactionId, categoryId) => {
    const normalizedTransactionId = String(transactionId || '').trim();
    const normalizedCategoryId = String(categoryId || '').trim();
    if (!normalizedTransactionId || !normalizedCategoryId) {
      return;
    }

    setError('');
    setUpdatingTransactionId(normalizedTransactionId);
    try {
      await transactionsAPI.update(normalizedTransactionId, { categoryId: normalizedCategoryId });
      const updated = await transactionsAPI.getById(normalizedTransactionId, { forceRefresh: true });
      setTransactions((current) =>
        current.map((transaction) => (
          String(transaction.id) === normalizedTransactionId ? updated : transaction
        ))
      );
      if (savedHighlightTimeoutRef.current) {
        clearTimeout(savedHighlightTimeoutRef.current);
      }
      setSavedTransactionId(normalizedTransactionId);
      savedHighlightTimeoutRef.current = setTimeout(() => {
        setSavedTransactionId('');
      }, 1200);
    } catch (err) {
      console.error('Error updating transaction category:', err);
      setError(err.response?.data?.error || 'Failed to update category');
    } finally {
      setUpdatingTransactionId('');
    }
  };

  const getCategoryTagText = (categoryId) => {
    if (!categoryId) {
      return 'Uncategorized';
    }
    return categoryDisplayNameById.get(String(categoryId)) || 'Uncategorized';
  };

  const closeTransactionActionMenu = () => {
    setActiveTransactionActionId('');
  };

  const toggleTransactionActionMenu = (transactionId) => {
    setShowSearchBar(false);
    setShowSearchFilters(false);
    setShowSortMenu(false);
    setActiveTransactionActionId((current) => (current === transactionId ? '' : transactionId));
  };
  const quickCategoryIdsForTransaction = (transaction) => (
    quickCategoryIds.filter((categoryId) => String(transaction.categoryId || '') !== categoryId)
  );

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && activeTransactionActionId) {
        closeTransactionActionMenu();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [activeTransactionActionId]);

  const handleTransactionCategorySelect = async (transaction, selectedValue) => {
    if (selectedValue !== ADD_CATEGORY_OPTION) {
      await handleCategoryChange(transaction.id, selectedValue);
      return;
    }
    setCreateCategoryContext({
      source: 'transaction',
      transactionId: String(transaction.id),
      manualItemIndex: null,
    });
    setNewCategoryForm({
      name: '',
      parentCategoryId: '',
    });
    setError('');
    setNewParentSubcategories([]);
    setNewSubcategoryInput('');
    setShowAllCreateSubcategoryRefs(false);
    setShowCreateCategoryModal(true);
  };
  const handleActionCategoryDropdownSelect = async (transaction, selectedValue) => {
    const normalizedValue = String(selectedValue || '').trim();
    if (!normalizedValue || !transaction) {
      return;
    }

    if (normalizedValue === ADD_CATEGORY_OPTION) {
      closeTransactionActionMenu();
      await handleTransactionCategorySelect(transaction, ADD_CATEGORY_OPTION);
      return;
    }

    // Backward-compatible support for legacy encoded values (parent:/child:)
    if (normalizedValue.includes(':')) {
      const [kind, rawId] = normalizedValue.split(':');
      const targetId = String(rawId || '').trim();
      if ((kind === 'parent' || kind === 'child') && targetId) {
        await handleTransactionCategorySelect(transaction, targetId);
        closeTransactionActionMenu();
      }
      return;
    }

    await handleTransactionCategorySelect(transaction, normalizedValue);
    closeTransactionActionMenu();
  };

  const handleManualItemCategorySelect = async (index, selectedValue) => {
    if (selectedValue !== ADD_CATEGORY_OPTION) {
      handleManualItemChange(index, 'categoryId', selectedValue);
      return;
    }
    setCreateCategoryContext({
      source: 'manual',
      transactionId: '',
      manualItemIndex: index,
      receiptItemId: '',
    });
    setNewCategoryForm({
      name: '',
      parentCategoryId: '',
    });
    setError('');
    setNewParentSubcategories([]);
    setNewSubcategoryInput('');
    setShowAllCreateSubcategoryRefs(false);
    setShowCreateCategoryModal(true);
  };

  const handleEditReceiptItemCategorySelect = (itemId, selectedValue) => {
    if (selectedValue !== ADD_CATEGORY_OPTION) {
      handleEditingReceiptItemChange(itemId, 'categoryId', selectedValue);
      return;
    }

    setCreateCategoryContext({
      source: 'receipt-edit',
      transactionId: '',
      manualItemIndex: null,
      receiptItemId: String(itemId),
    });
    setNewCategoryForm({
      name: '',
      parentCategoryId: '',
    });
    setError('');
    setNewParentSubcategories([]);
    setNewSubcategoryInput('');
    setShowAllCreateSubcategoryRefs(false);
    setShowCreateCategoryModal(true);
  };

  const closeCreateCategoryModal = () => {
    setShowCreateCategoryModal(false);
    setIsCreateCategoryNameFocused(false);
    setIsCreateCategorySelectedFromSuggestion(false);
    setNewCategoryForm({
      name: '',
      parentCategoryId: '',
    });
    setError('');
    setNewParentSubcategories([]);
    setNewSubcategoryInput('');
    setShowAllCreateSubcategoryRefs(false);
    setCreateCategoryContext({
      source: 'transaction',
      transactionId: '',
      manualItemIndex: null,
      receiptItemId: '',
    });
  };

  const addCreateSubcategoryChip = (rawValue) => {
    const candidate = String(rawValue || '').trim();
    if (!candidate) {
      return;
    }
    const normalizedCandidate = toComparableText(candidate);
    setNewParentSubcategories((current) => (
      current.some((entry) => toComparableText(entry) === normalizedCandidate)
        ? current
        : [...current, candidate]
    ));
  };

  const removeCreateSubcategoryChip = (chipName) => {
    const normalizedChip = toComparableText(chipName);
    setNewParentSubcategories((current) => current.filter((entry) => toComparableText(entry) !== normalizedChip));
  };

  const assignCategoryToManualItem = (itemIndex, categoryId) => {
    if (!Number.isInteger(itemIndex) || itemIndex < 0) {
      return;
    }

    setManualForm((current) => ({
      ...current,
      items: current.items.map((item, index) => (
        index === itemIndex ? { ...item, categoryId: String(categoryId) } : item
      )),
    }));
  };

  const assignCategoryToEditingReceiptItem = (itemId, categoryId) => {
    if (!itemId) {
      return;
    }

    setEditingReceiptDraft((current) => ({
      ...current,
      items: (current.items || []).map((item) => (
        String(item.id) === String(itemId)
          ? { ...item, categoryId: String(categoryId) }
          : item
      )),
    }));
  };

  const handleCreateCategoryFromModal = async () => {
    const trimmedName = String(newCategoryForm.name || '').trim();
    if (!trimmedName) {
      setError('Please enter a category name');
      return;
    }

    const selectedParentCategoryId = String(newCategoryForm.parentCategoryId || '').trim() || '';
    const normalizedName = toComparableText(trimmedName);
    const childNames = [...new Set(
      [...newParentSubcategories, newSubcategoryInput]
        .map((name) => String(name || '').trim())
        .filter(Boolean)
    )];
    const hasChildNames = childNames.length > 0;
    const existingMainCategory = categories.find(
      (category) =>
        !category.parentCategoryId &&
        toComparableText(category.name) === normalizedName
    );
    const existingCategory = categories.find(
      (category) =>
        toComparableText(category.name) === normalizedName &&
        String(category.parentCategoryId || '') === selectedParentCategoryId
    );

    const assignCategory = async (category) => {
      if (createCategoryContext.source === 'manual') {
        assignCategoryToManualItem(createCategoryContext.manualItemIndex, category.id);
        return;
      }
      if (createCategoryContext.source === 'receipt-edit' && createCategoryContext.receiptItemId) {
        assignCategoryToEditingReceiptItem(createCategoryContext.receiptItemId, category.id);
        return;
      }
      if (createCategoryContext.transactionId) {
        await handleCategoryChange(createCategoryContext.transactionId, String(category.id));
      }
    };

    if (existingCategory && !(hasChildNames && !selectedParentCategoryId)) {
      if (isCreateCategorySelectedFromSuggestion) {
        try {
          await assignCategory(existingCategory);
          closeCreateCategoryModal();
          setSuccess(`Category "${existingCategory.name}" selected.`);
        } catch (err) {
          console.error('Error assigning selected existing category from modal:', err);
          setError(err.response?.data?.error || 'Failed to assign category');
        }
        return;
      }
      setError(`Category "${existingCategory.name}" already exists. Select it from the suggestion list.`);
      return;
    }

    setCreatingCategory(true);
    setError('');
    try {
      const usedColors = new Set(categories.map((category) => normalizeHexColor(category.color)));
      const createdCategories = [];
      let createdCategory = null;

      const reserveColor = () => {
        const color = getNextUniqueCategoryColor(usedColors);
        usedColors.add(normalizeHexColor(color));
        return color;
      };

      if (selectedParentCategoryId) {
        if (
          existingMainCategory &&
          String(existingMainCategory.id) !== String(selectedParentCategoryId)
        ) {
          const selectedParentName = categoriesById.get(String(selectedParentCategoryId))?.name || 'selected parent';
          setError(
            `Main category "${existingMainCategory.name}" already exists. Choose "${selectedParentName}" from Category Name suggestions or use a new name.`
          );
          setCreatingCategory(false);
          return;
        }

        createdCategory = await categoriesAPI.create({
          name: trimmedName,
          parentCategoryId: selectedParentCategoryId,
          color: reserveColor(),
        });
        createdCategories.push(createdCategory);
      } else {
        let parentCategory = null;
        if (existingMainCategory) {
          if (!hasChildNames) {
            setError(`Main category "${existingMainCategory.name}" already exists. Select it from the suggestion list.`);
            setCreatingCategory(false);
            return;
          }
          parentCategory = existingMainCategory;
        } else {
          parentCategory = await categoriesAPI.create({
            name: trimmedName,
            color: reserveColor(),
          });
          createdCategories.push(parentCategory);
        }

        createdCategory = parentCategory;
        const parentId = String(parentCategory.id);

        const existingChildNames = new Set(
          categories
            .filter((category) => String(category.parentCategoryId || '') === parentId)
            .map((category) => toComparableText(category.name))
        );
        const missingChildNames = childNames.filter((name) => !existingChildNames.has(toComparableText(name)));
        if (hasChildNames && existingMainCategory && missingChildNames.length === 0) {
          const subcategoryLabel = childNames.length === 1 ? 'subcategory' : 'subcategories';
          setError(
            `Main category "${parentCategory.name}" and ${subcategoryLabel} "${childNames.join(', ')}" already exist.`
          );
          return;
        }
        for (const childName of missingChildNames) {
          const createdChild = await categoriesAPI.create({
            name: childName,
            parentCategoryId: parentId,
            color: reserveColor(),
          });
          createdCategories.push(createdChild);
        }
      }

      if (createdCategories.length > 0) {
        setCategories((current) => [...current, ...createdCategories]);
      }
      await assignCategory(createdCategory);
      if (createdCategories.length === 0) {
        setSuccess(`Using existing category "${createdCategory.name}".`);
      } else {
        const createdSubcategoryCount = Math.max(createdCategories.length - 1, 0);
        const summarySuffix = createdSubcategoryCount > 0
          ? ` with ${createdSubcategoryCount} subcategor${createdSubcategoryCount === 1 ? 'y' : 'ies'}`
          : '';
        setSuccess(`Category "${createdCategory.name}" created${summarySuffix}`);
      }
      closeCreateCategoryModal();
    } catch (err) {
      console.error('Error creating category from transaction modal:', err);
      setError(err.response?.data?.error || 'Failed to create category');
    } finally {
      setCreatingCategory(false);
    }
  };

  const undoDelete = (transactionId) => {
    const key = String(transactionId);
    setPendingDeletes((current) => {
      const pendingDelete = current.find((entry) => entry.id === key);
      if (!pendingDelete) {
        return current;
      }

      const timeoutId = deleteTimeoutsRef.current.get(key);
      if (timeoutId) {
        clearTimeout(timeoutId);
        deleteTimeoutsRef.current.delete(key);
      }

      setTransactions((existingTransactions) => {
        if (existingTransactions.some((transaction) => String(transaction.id) === key)) {
          return existingTransactions;
        }
        const nextTransactions = [...existingTransactions];
        const insertionIndex = Math.min(Math.max(pendingDelete.index, 0), nextTransactions.length);
        nextTransactions.splice(insertionIndex, 0, pendingDelete.transaction);
        return nextTransactions;
      });

      return current.filter((entry) => entry.id !== key);
    });
    setPendingDeleteToast((current) => (current && current.id === key ? null : current));
    setSuccess('Deletion undone');
  };

  const handleDelete = (id) => {
    const transactionToDelete = transactions.find((transaction) => String(transaction.id) === String(id));
    if (!transactionToDelete) {
      return;
    }

    const transactionKey = String(id);
    const transactionIndex = transactions.findIndex((transaction) => String(transaction.id) === transactionKey);
    setTransactions((current) => current.filter((transaction) => String(transaction.id) !== transactionKey));
    setPendingDeletes((current) => {
      const withoutCurrent = current.filter((entry) => entry.id !== transactionKey);
      return [
        ...withoutCurrent,
        { id: transactionKey, transaction: transactionToDelete, index: transactionIndex },
      ];
    });
    setPendingDeleteToast({
      id: transactionKey,
      itemName: transactionToDelete.itemName || 'transaction item',
    });

    if (deleteTimeoutsRef.current.has(transactionKey)) {
      clearTimeout(deleteTimeoutsRef.current.get(transactionKey));
    }

    const timeoutId = setTimeout(async () => {
      try {
        await transactionsAPI.delete(id);
      } catch (err) {
        console.error('Error deleting transaction:', err);
        setError('Failed to delete transaction');
        undoDelete(id);
      } finally {
        deleteTimeoutsRef.current.delete(transactionKey);
        setPendingDeletes((current) => current.filter((entry) => entry.id !== transactionKey));
        setPendingDeleteToast((current) => (current && current.id === transactionKey ? null : current));
      }
    }, DELETE_UNDO_MS);
    deleteTimeoutsRef.current.set(transactionKey, timeoutId);
  };

  if (loading && lastLoadedTransactionCount === null) {
    return (
      <div className="space-y-4 animate-pulse">
        {[1, 2, 3].map((skeleton) => (
          <div key={`receipt-skeleton-${skeleton}`} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <div className="h-3 w-24 rounded bg-gray-200"></div>
                <div className="h-5 w-40 rounded bg-gray-200"></div>
              </div>
              <div className="space-y-2">
                <div className="h-3 w-16 rounded bg-gray-200"></div>
                <div className="h-5 w-20 rounded bg-gray-200"></div>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              <div className="h-10 rounded bg-gray-100"></div>
              <div className="h-10 rounded bg-gray-100"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={isMobileView ? 'pb-28' : ''}>
      {isMobileView && (
        <div
          className={`fixed left-0 right-0 z-30 px-3 transition-all duration-200 ${
            showFloatingTransactionsHeader ? 'top-16 opacity-100' : 'pointer-events-none top-12 opacity-0'
          }`}
        >
          <div className="rounded-xl border border-gray-200 bg-white/95 p-2 shadow-lg backdrop-blur">
            <div className="mb-2 px-1 text-sm font-semibold text-gray-900">Transactions</div>
            <div className="grid w-full grid-cols-2 gap-1 rounded-full border border-slate-200 bg-slate-100 p-1">
              <button
                type="button"
                onClick={() => setActiveTransactionsTab('ledger')}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                  activeTransactionsTab === 'ledger'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:bg-white/60'
                }`}
              >
                Actual
              </button>
              <button
                type="button"
                onClick={() => setActiveTransactionsTab('upcoming')}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                  activeTransactionsTab === 'upcoming'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:bg-white/60'
                }`}
              >
                Future
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-4 rounded-xl border border-gray-200 bg-white p-3 shadow-sm sm:mb-5 sm:p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Transactions</h1>
          </div>
          <div ref={topAddMenuRef} className="relative">
            <button
              type="button"
              onClick={toggleTopAddMenu}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-blue-700 sm:px-4"
            >
              <PlusIcon className="h-4 w-4" aria-hidden="true" />
              Add Transaction
            </button>
            {activeAddMenu === 'top' && (
              <div className="absolute right-0 mt-2 w-60 rounded-lg border border-gray-200 bg-white p-2 shadow-lg z-20">
                <button
                  type="button"
                  onClick={() => {
                    setActiveAddMenu(null);
                    navigate('/upload');
                  }}
                  className="w-full rounded-md px-3 py-2 text-left hover:bg-gray-50"
                >
                  <p className="text-sm font-semibold text-gray-900">Upload Receipt</p>
                  <p className="text-xs text-gray-500">Go to receipt upload and AI extraction.</p>
                </button>
                <button
                  type="button"
                  onClick={openManualModal}
                  className="w-full rounded-md px-3 py-2 text-left hover:bg-gray-50"
                >
                  <p className="text-sm font-semibold text-gray-900">Add Transaction Detail</p>
                  <p className="text-xs text-gray-500">Open a form to add transaction details.</p>
                </button>
                <button
                  type="button"
                  onClick={triggerHistoricalImportFilePicker}
                  className="w-full rounded-md px-3 py-2 text-left hover:bg-gray-50"
                >
                  <p className="text-sm font-semibold text-gray-900">Import Historical Data</p>
                  <p className="text-xs text-gray-500">Upload CSV or TSV from Excel, Sheets, or Numbers.</p>
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="mt-3 grid w-full grid-cols-2 gap-1 rounded-full border border-slate-200 bg-slate-100 p-1">
          <button
            type="button"
            onClick={() => setActiveTransactionsTab('ledger')}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
              activeTransactionsTab === 'ledger'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:bg-white/60'
            }`}
          >
            Actual
          </button>
          <button
            type="button"
            onClick={() => setActiveTransactionsTab('upcoming')}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
              activeTransactionsTab === 'upcoming'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:bg-white/60'
            }`}
          >
            Future
          </button>
        </div>
      </div>

      {loadError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p>{loadError}</p>
            <button
              type="button"
              onClick={fetchData}
              className="rounded border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
            >
              Retry Load
            </button>
          </div>
        </div>
      )}

      {!loadError && lastLoadedTransactionCount === 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          No transactions were returned for this signed-in account. If this seems wrong, log out and log back in.
        </div>
      )}

      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-3 space-y-3 sm:p-4 sm:space-y-4">
        <div className="space-y-2">
          {!showSearchBar ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setActiveTransactionActionId('');
                  setExpandedReceipts({});
                  setShowSearchBar(true);
                }}
                className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-sm text-gray-500 hover:bg-gray-50 sm:px-4 sm:py-2.5"
              >
                Search
              </button>
              <div className="relative" ref={sortMenuRef}>
                <button
                  type="button"
                  data-sort-trigger
                  onClick={() => setShowSortMenu((current) => !current)}
                  className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white p-2 text-gray-600 hover:bg-gray-50"
                  aria-label="Sort transactions"
                  aria-expanded={showSortMenu}
                  aria-haspopup="menu"
                >
                  <BarsArrowUpIcon className="h-4 w-4 text-gray-500" aria-hidden="true" />
                </button>
                {showSortMenu && (
                  <div
                    data-sort-menu
                    className="absolute right-0 top-10 z-20 w-44 rounded-lg border border-gray-200 bg-white p-1.5 shadow-lg"
                  >
                    {[
                      { key: 'latest', label: 'Latest' },
                      { key: 'oldest', label: 'Oldest' },
                      { key: 'lowest', label: 'Lowest Amount' },
                      { key: 'highest', label: 'Highest Amount' },
                    ].map((option) => (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => {
                          setSortOrder(option.key);
                          setShowSortMenu(false);
                        }}
                        className={`w-full rounded px-2 py-1.5 text-left text-sm ${
                          sortOrder === option.key
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        role="menuitem"
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={searchTextInput}
                    onChange={(e) => setSearchTextInput(e.target.value)}
                    placeholder="Search any text (store, item, category, payment, date, amount)"
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 pr-9 text-left text-sm text-gray-700 sm:px-4 sm:py-2.5"
                  />
                  {searchTextInput && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchTextInput('');
                        setSearchText('');
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-xs text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                      aria-label="Clear text search"
                    >
                      x
                    </button>
                  )}
                </div>
                <div className="relative" ref={sortMenuRef}>
                  <button
                    type="button"
                    data-sort-trigger
                    onClick={() => setShowSortMenu((current) => !current)}
                    className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white p-2 text-gray-600 hover:bg-gray-50"
                    aria-label="Sort transactions"
                    aria-expanded={showSortMenu}
                    aria-haspopup="menu"
                  >
                    <BarsArrowUpIcon className="h-4 w-4 text-gray-500" aria-hidden="true" />
                  </button>
                  {showSortMenu && (
                    <div
                      data-sort-menu
                      className="absolute right-0 top-10 z-20 w-44 rounded-lg border border-gray-200 bg-white p-1.5 shadow-lg"
                    >
                      {[
                        { key: 'latest', label: 'Latest' },
                        { key: 'oldest', label: 'Oldest' },
                        { key: 'lowest', label: 'Lowest Amount' },
                        { key: 'highest', label: 'Highest Amount' },
                      ].map((option) => (
                        <button
                          key={option.key}
                          type="button"
                          onClick={() => {
                            setSortOrder(option.key);
                            setShowSortMenu(false);
                          }}
                          className={`w-full rounded px-2 py-1.5 text-left text-sm ${
                            sortOrder === option.key
                              ? 'bg-blue-50 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                          role="menuitem"
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowSearchFilters((current) => !current)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
              >
                {showSearchFilters ? 'Hide Advanced Filters' : 'Show Advanced Filters'}
              </button>
            </>
          )}
          {filtersAreActive && (
            <span className="inline-flex text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-1">
              Filters active
            </span>
          )}
          {loading && lastLoadedTransactionCount !== null && (
            <span className="inline-flex text-xs text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2 py-1">
              Updating results...
            </span>
          )}
        </div>

        {showSearchFilters && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'amount', label: 'Amount' },
                { key: 'date', label: 'Date' },
                { key: 'category', label: 'Category' },
                { key: 'payment', label: 'Payment Method' },
              ].map((filterOption) => (
                <button
                  key={filterOption.key}
                  type="button"
                  onClick={() => setActiveSearchFilter(filterOption.key)}
                  className={`rounded px-3 py-1.5 text-sm border ${
                    activeSearchFilter === filterOption.key
                      ? 'bg-blue-50 text-blue-700 border-blue-200'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {filterOption.label}
                </button>
              ))}
            </div>
            {activeSearchFilter === 'amount' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  type="number"
                  step="0.01"
                  value={searchAmountMin}
                  onChange={(e) => setSearchAmountMin(e.target.value)}
                  placeholder="Amount Min"
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                />
                <input
                  type="number"
                  step="0.01"
                  value={searchAmountMax}
                  onChange={(e) => setSearchAmountMax(e.target.value)}
                  placeholder="Amount Max"
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            )}

            {activeSearchFilter === 'date' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <DatePicker
                  selected={filterDateFromValue}
                  onChange={(date) => {
                    if (!date) {
                      setFilterDateFrom('');
                      return;
                    }
                    const timezoneOffsetMs = date.getTimezoneOffset() * 60000;
                    const localIsoDate = new Date(date.getTime() - timezoneOffsetMs).toISOString().split('T')[0];
                    setFilterDateFrom(localIsoDate);
                  }}
                  dateFormat="MM-dd-yyyy"
                  withPortal
                  portalId="root"
                  wrapperClassName="w-full"
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  placeholderText="From date"
                  isClearable
                />
                <DatePicker
                  selected={filterDateToValue}
                  onChange={(date) => {
                    if (!date) {
                      setFilterDateTo('');
                      return;
                    }
                    const timezoneOffsetMs = date.getTimezoneOffset() * 60000;
                    const localIsoDate = new Date(date.getTime() - timezoneOffsetMs).toISOString().split('T')[0];
                    setFilterDateTo(localIsoDate);
                  }}
                  minDate={filterDateFromValue || undefined}
                  dateFormat="MM-dd-yyyy"
                  withPortal
                  portalId="root"
                  wrapperClassName="w-full"
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  placeholderText="To date"
                  isClearable
                />
              </div>
            )}

            {activeSearchFilter === 'category' && (
              <SearchableSelect
                value={String(filterCategory || '')}
                onChange={setFilterCategory}
                options={categoryFilterOptions}
                placeholder="Search categories"
                ariaLabel="Search transaction categories"
                inputClassName="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none"
                menuClassName="max-h-56"
              />
            )}

            {activeSearchFilter === 'payment' && (
              <select
                value={filterPaymentMethod}
                onChange={(e) => setFilterPaymentMethod(e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">All payment methods</option>
                {paymentMethods.map((method) => (
                  <option key={method} value={method}>
                    {method}
                  </option>
                ))}
              </select>
            )}

            <div>
              <button
                type="button"
                onClick={clearAllFilters}
                className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Clear All Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {activeTransactionsTab === 'ledger' ? (
        groupedReceipts.length > 0 ? (
        <div className="space-y-4">
          {groupedReceipts.map((receipt) => {
            const receiptKey = String(receipt.key);
            const isExpanded = Object.prototype.hasOwnProperty.call(expandedReceipts, receipt.key)
              ? Boolean(expandedReceipts[receipt.key])
              : filtersAreActive;
            const shouldShowAllItems = Boolean(showAllItemsByReceipt[receiptKey]);
            const hasHiddenItems = receipt.items.length > 10;
            const visibleReceiptItems = shouldShowAllItems
              ? receipt.items
              : receipt.items.slice(0, 10);
            const effectiveTax = receipt.hasTaxLineItem ? 0 : receipt.taxAmount;
            const receiptTotal = Number((receipt.totalAmount + effectiveTax).toFixed(2));
            const receiptDate = toIsoDate(receipt.transactionDate);
            const editedReceiptSubtotal = Number(
              ((editingReceiptDraft.items || []).reduce((sum, item) => sum + toNumber(item.itemTotal), 0)).toFixed(2)
            );
            const editedReceiptTotal = Number((editedReceiptSubtotal + effectiveTax).toFixed(2));

            return (
              <div
                key={receipt.key}
                className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow"
              >
                {editingReceiptKey === String(receipt.key) ? (
                  <div className="space-y-3 p-4">
                    <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-gray-900">
                            {editingReceiptDraft.storeName || receipt.storeName || 'Receipt'}
                          </p>
                          <p className="mt-0.5 text-xs text-gray-500">
                            {formatDate(editingReceiptDraft.transactionDate || receiptDate || new Date())}
                            {editingReceiptDraft.paymentMethod ? ` | ${editingReceiptDraft.paymentMethod}` : ''}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Total</p>
                          <p className="text-sm font-semibold text-gray-900">{formatCurrency(editedReceiptTotal)}</p>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between gap-3">
                        <div className="inline-flex rounded-full border border-gray-200 bg-white p-1 text-xs font-medium">
                          <span
                            className={`rounded-full px-2.5 py-1 ${
                              editingReceiptStep === 1 ? 'bg-blue-600 text-white' : 'text-gray-600'
                            }`}
                          >
                            Details
                          </span>
                          <span
                            className={`rounded-full px-2.5 py-1 ${
                              editingReceiptStep === 2 ? 'bg-blue-600 text-white' : 'text-gray-600'
                            }`}
                          >
                            Items
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={cancelEditingReceipt}
                          disabled={savingReceiptDraft}
                          className="rounded border border-gray-300 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:bg-gray-100"
                        >
                          Close
                        </button>
                      </div>
                    </div>

                    {editingReceiptStep === 1 ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          <label className="text-xs font-medium text-gray-600">
                            Date
                            <DatePicker
                              selected={editingReceiptTransactionDateValue}
                              onChange={(date) => {
                                if (!date) {
                                  handleEditingReceiptDraftChange('transactionDate', '');
                                  return;
                                }
                                const timezoneOffsetMs = date.getTimezoneOffset() * 60000;
                                const localIsoDate = new Date(date.getTime() - timezoneOffsetMs).toISOString().split('T')[0];
                                handleEditingReceiptDraftChange('transactionDate', localIsoDate);
                              }}
                              dateFormat="MM-dd-yyyy"
                              withPortal
                              portalId="root"
                              wrapperClassName="mt-1 w-full"
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                              placeholderText="Transaction Date"
                            />
                          </label>
                          <label className="text-xs font-medium text-gray-600">
                            Merchant
                            <input
                              type="text"
                              value={editingReceiptDraft.storeName}
                              onChange={(event) => handleEditingReceiptDraftChange('storeName', event.target.value)}
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                              placeholder="Store Name"
                            />
                          </label>
                          <label className="text-xs font-medium text-gray-600 md:col-span-2">
                            Location
                            <input
                              type="text"
                              value={editingReceiptDraft.storeLocation}
                              onChange={(event) =>
                                handleEditingReceiptDraftChange('storeLocation', event.target.value)
                              }
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                              placeholder="Store Location"
                            />
                          </label>
                          <div className="md:col-span-2">
                            <p className="mb-1 text-xs font-medium text-gray-600">Payment</p>
                            <div className="inline-flex overflow-hidden rounded-full border border-gray-300 bg-white p-1">
                              <button
                                type="button"
                                onClick={() => handleEditingReceiptDraftChange('paymentMethod', 'Credit Card')}
                                className={`rounded-full px-3 py-1 text-xs transition ${
                                  editingReceiptDraft.paymentMethod === 'Credit Card'
                                    ? 'bg-blue-600 text-white'
                                    : 'text-gray-700 hover:bg-gray-100'
                                }`}
                              >
                                Credit Card
                              </button>
                              <button
                                type="button"
                                onClick={() => handleEditingReceiptDraftChange('paymentMethod', 'Cash')}
                                className={`rounded-full px-3 py-1 text-xs transition ${
                                  editingReceiptDraft.paymentMethod === 'Cash'
                                    ? 'bg-blue-600 text-white'
                                    : 'text-gray-700 hover:bg-gray-100'
                                }`}
                              >
                                Cash
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-end gap-2 border-t border-gray-200 pt-3">
                          <button
                            type="button"
                            onClick={() => setEditingReceiptStep(2)}
                            className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
                          >
                            Next: Items
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2 border-t border-gray-200 pt-3">
                        <div className="hidden grid-cols-[minmax(0,2.2fr)_minmax(4rem,0.7fr)_minmax(5rem,0.9fr)_minmax(0,2fr)] gap-2 px-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 md:grid">
                          <span>Item</span>
                          <span>Qty</span>
                          <span>Amount</span>
                          <span>Category</span>
                        </div>
                        {(editingReceiptDraft.items || []).map((item, index) => (
                          <div
                            key={`edit-receipt-item-${item.id}`}
                            className="grid grid-cols-1 gap-2 rounded-lg border border-gray-200 p-2 md:grid-cols-[minmax(0,2.2fr)_minmax(4rem,0.7fr)_minmax(5rem,0.9fr)_minmax(0,2fr)]"
                          >
                            <input
                              type="text"
                              value={item.itemName}
                              onChange={(event) =>
                                handleEditingReceiptItemChange(item.id, 'itemName', event.target.value)
                              }
                              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                              placeholder={`Item ${index + 1} name`}
                            />
                            <input
                              type="number"
                              min="1"
                              step="1"
                              value={item.quantity}
                              onChange={(event) =>
                                handleEditingReceiptItemChange(item.id, 'quantity', event.target.value)
                              }
                              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                              placeholder="Qty"
                            />
                            <input
                              type="number"
                              step="0.01"
                              value={item.itemTotal}
                              onChange={(event) =>
                                handleEditingReceiptItemChange(item.id, 'itemTotal', event.target.value)
                              }
                              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                              placeholder="Amount"
                            />
                            <SearchableSelect
                              value={String(item.categoryId || '')}
                              onChange={(selectedValue) => handleEditReceiptItemCategorySelect(item.id, selectedValue)}
                              options={categorySelectOptions}
                              placeholder="Search category"
                              ariaLabel="Search category for receipt item"
                              inputClassName="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-400 focus:outline-none"
                              menuClassName="max-h-56"
                            />
                          </div>
                        ))}

                        <div className="sticky bottom-0 flex items-center justify-between gap-2 border-t border-gray-200 bg-white pt-3 pb-[calc(0.5rem+env(safe-area-inset-bottom))]">
                          <button
                            type="button"
                            onClick={() => setEditingReceiptStep(1)}
                            disabled={savingReceiptDraft}
                            className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:bg-gray-100"
                          >
                            Back
                          </button>
                          <button
                            type="button"
                            onClick={saveEditedReceipt}
                            disabled={savingReceiptDraft}
                            className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:bg-blue-400"
                          >
                            {savingReceiptDraft ? 'Saving...' : 'Save Changes'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    {isExpanded && (
                      <div className="flex items-center justify-between gap-3 px-4 pb-1 pt-3">
                        <p className="inline-flex items-center gap-1.5 truncate text-xs text-gray-500">
                          <CalendarDaysIcon className="h-3.5 w-3.5 text-gray-400" />
                          {receiptDate ? formatDate(receiptDate) : 'Unknown date'}
                        </p>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleCopyReceipt(receipt)}
                            className="inline-flex h-7 items-center rounded-full border border-blue-200 bg-blue-50 px-3 text-xs font-medium text-blue-700 transition hover:bg-blue-100"
                            aria-label="Copy receipt as new"
                            title="Copy receipt as new"
                          >
                            Copy
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              persistReceiptDetailSelection(receipt);
                              navigate(
                                `/transactions/receipt/edit?key=${encodeURIComponent(receiptKey)}`,
                                { state: { receipt } }
                              );
                            }}
                            className="inline-flex h-7 items-center rounded-full bg-gray-100 px-3 text-xs font-medium text-gray-700 transition hover:bg-gray-200"
                            aria-label="Edit receipt"
                            title="Edit receipt"
                          >
                            Edit
                          </button>
                        </div>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => toggleReceipt(receipt.key)}
                      className={`w-full p-4 text-left transition hover:bg-gray-50 ${isExpanded ? 'pt-2' : ''}`}
                    >
                      <div className="flex items-stretch justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <h2 className="truncate text-sm font-semibold text-gray-900" title={receipt.storeName || 'Unknown store'}>
                            {receipt.storeName || 'Unknown store'}
                          </h2>
                          <p className="mt-0.5 truncate text-sm text-gray-700">
                            {isExpanded
                              ? (receipt.storeLocation || 'Header tap to collapse')
                              : `${receiptDate ? formatDate(receiptDate) : 'Unknown date'}${receipt.storeLocation ? ` • ${receipt.storeLocation}` : ''}`}
                          </p>
                          <p className="mt-0.5 truncate text-xs text-gray-600">
                            {receipt.items.length} item{receipt.items.length === 1 ? '' : 's'}
                          </p>
                        </div>
                        <div className="shrink-0 flex flex-col items-end justify-between text-right">
                          <p className="text-sm font-semibold text-gray-900">{formatCurrency(receiptTotal)}</p>
                          <span className="inline-flex items-center justify-end text-gray-500">
                            {isExpanded ? (
                              <ChevronUpIcon className="h-3 w-3" />
                            ) : (
                              <ChevronDownIcon className="h-3 w-3" />
                            )}
                          </span>
                        </div>
                      </div>
                    </button>
                  </div>
                )}

                {isExpanded && editingReceiptKey !== String(receipt.key) && (
                  <div className="receipt-unroll-wrapper receipt-unroll-wrapper-open border-t border-gray-200">
                    <div className="md:hidden space-y-2 p-3">
                      <div className="receipt-unroll-line grid grid-cols-[minmax(0,2.4fr)_minmax(1.75rem,0.55fr)_minmax(3.75rem,0.85fr)_minmax(0,2.3fr)_auto] items-center gap-1.5 px-1 text-[10px] font-medium uppercase tracking-wide text-gray-500">
                        <span>Item</span>
                        <span className="text-center">Qty</span>
                        <span className="text-center">Amt</span>
                        <span className="text-center">Category</span>
                        <span className="sr-only">Actions</span>
                      </div>
                      {visibleReceiptItems.map((transaction, itemIndex) => (
                        <div
                          key={transaction.id}
                          className={`receipt-unroll-line rounded-lg border bg-white p-2 transition ${
                            savedTransactionId === String(transaction.id)
                              ? 'border-green-300 bg-green-50/60'
                              : 'border-gray-200'
                          } ${
                            activeTransactionActionId === transaction.id ? 'relative z-30' : ''
                          }`}
                          style={{ animationDelay: `${Math.min(itemIndex * 45, 360)}ms` }}
                        >
                          <div className="grid grid-cols-[minmax(0,2.4fr)_minmax(1.75rem,0.55fr)_minmax(3.75rem,0.85fr)_minmax(0,2.3fr)_auto] items-center gap-1.5 text-xs">
                            <p
                              className="min-w-0 overflow-hidden font-medium leading-4 text-gray-900 [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]"
                              title={transaction.itemName}
                            >
                              {transaction.itemName}
                            </p>
                            <p className="text-center text-gray-700 whitespace-nowrap">{transaction.quantity}</p>
                            <p className="text-center font-medium text-gray-900 whitespace-nowrap">{formatCurrency(transaction.itemTotal)}</p>
                            <span
                              className="min-w-0 overflow-hidden rounded-full bg-gray-100 px-2 py-1 text-center text-[10px] leading-4 text-gray-700 [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]"
                              title={getCategoryTagText(transaction.categoryId)}
                            >
                              {getCategoryTagText(transaction.categoryId)}
                            </span>
                            <div className="relative">
                              <button
                                type="button"
                                data-transaction-action-trigger
                                onClick={() => toggleTransactionActionMenu(transaction.id)}
                                className="shrink-0 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                                title="Transaction actions"
                                aria-label="Transaction actions"
                              >
                                <EllipsisVerticalIcon className="h-4 w-4" aria-hidden="true" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="hidden md:block overflow-x-auto">
                      <table className="min-w-full table-fixed divide-y divide-gray-200">
                        <thead className="receipt-unroll-line bg-gray-50">
                          <tr>
                            <th className="w-[42%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                            <th className="w-16 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                            <th className="w-28 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                            <th className="w-[30%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                            <th className="w-20 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {visibleReceiptItems.map((transaction, rowIndex) => (
                            <tr
                              key={transaction.id}
                              className={`receipt-unroll-line ${
                                savedTransactionId === String(transaction.id) ? 'bg-green-50/60' : ''
                              } ${activeTransactionActionId === transaction.id ? 'relative z-30' : ''}`}
                              style={{ animationDelay: `${Math.min(rowIndex * 45, 360)}ms` }}
                            >
                              <td className="px-4 py-3 text-sm text-gray-900">
                                <p className="truncate" title={transaction.itemName}>
                                  {transaction.itemName}
                                </p>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">{transaction.quantity}</td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                {formatCurrency(transaction.itemTotal)}
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className="inline-flex max-w-full items-center overflow-hidden rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-700 text-ellipsis whitespace-nowrap"
                                  title={getCategoryTagText(transaction.categoryId)}
                                >
                                  {getCategoryTagText(transaction.categoryId)}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <div className="relative inline-block">
                                  <button
                                    type="button"
                                    data-transaction-action-trigger
                                    onClick={() => toggleTransactionActionMenu(transaction.id)}
                                    className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                                    title="Transaction actions"
                                    aria-label="Transaction actions"
                                  >
                                    <EllipsisVerticalIcon className="h-4 w-4" aria-hidden="true" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {(hasHiddenItems || shouldShowAllItems) && (
                      <div className="receipt-unroll-line flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 px-4 py-2 text-xs text-gray-600">
                        <p>
                          Showing {visibleReceiptItems.length} of {receipt.items.length} items
                        </p>
                        <div className="flex items-center gap-2">
                          {hasHiddenItems && (
                            <button
                              type="button"
                              onClick={() =>
                                setShowAllItemsByReceipt((current) => ({
                                  ...current,
                                  [receiptKey]: !current[receiptKey],
                                }))
                              }
                              className="rounded border border-gray-300 px-2 py-1 font-medium text-gray-700 hover:bg-gray-50"
                            >
                              {shouldShowAllItems ? 'Show first 10' : `Show all ${receipt.items.length}`}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => navigate(`/transactions/receipt/${encodeURIComponent(receiptKey)}`)}
                            className="rounded border border-blue-200 bg-blue-50 px-2 py-1 font-medium text-blue-700 hover:bg-blue-100"
                          >
                            View all items
                          </button>
                        </div>
                      </div>
                    )}

                    <div
                      className="receipt-unroll-line sticky bottom-0 z-10 flex flex-col items-start gap-1 border-t border-gray-200 bg-gray-50 px-4 py-3 text-sm md:static md:flex-row md:justify-end md:gap-8 md:border-t-0"
                      style={{ animationDelay: `${Math.min(receipt.items.length * 45 + 60, 460)}ms` }}
                    >
                      <span className="text-gray-600">
                        Subtotal: <span className="font-semibold text-gray-900">{formatCurrency(receipt.totalAmount)}</span>
                      </span>
                      <span className="text-gray-600">
                        Tax: <span className="font-semibold text-gray-900">{formatCurrency(effectiveTax)}</span>
                      </span>
                      <span className="text-gray-600">
                        Total: <span className="font-semibold text-gray-900">{formatCurrency(receiptTotal)}</span>
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        ) : (
          <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
            <div className="text-6xl mb-4">
              {loadError ? 'Could Not Load Transactions' : (filtersAreActive ? 'No Matching Receipts' : 'No Transactions Yet')}
            </div>
            <p className="text-gray-600 text-lg mb-2">
              {loadError
                ? 'We could not fetch transactions from the server for this session.'
                : filtersAreActive
                ? 'No transactions match your current filters.'
                : 'Add your first receipt to start tracking transactions.'}
            </p>
            {loadError ? (
              <button
                type="button"
                onClick={fetchData}
                className="rounded-lg bg-blue-600 px-6 py-2 text-white hover:bg-blue-700 transition"
              >
                Retry Load
              </button>
            ) : filtersAreActive ? (
              <>
                <p className="text-gray-500 text-sm mb-3">Try removing one or more filters:</p>
                <div className="mb-4 flex flex-wrap justify-center gap-2">
                  {activeFilterChips.map((chip) => (
                    <button
                      key={`empty-chip-${chip.key}`}
                      type="button"
                      onClick={() => clearSingleFilter(chip.key)}
                      className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs text-blue-700 hover:bg-blue-100"
                    >
                      {chip.label} x
                    </button>
                  ))}
                </div>
                <button
                  onClick={clearAllFilters}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
                >
                  Clear Filters
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={openManualModal}
                className="rounded-lg bg-blue-600 px-6 py-2 text-white hover:bg-blue-700 transition"
              >
                Add First Transaction
              </button>
            )}
          </div>
        )
      ) : (
        upcomingRecurringLoading && !hasLoadedUpcomingRecurring ? (
          <div className="space-y-3">
            {[1, 2, 3].map((skeleton) => (
              <div
                key={`upcoming-recurring-skeleton-${skeleton}`}
                className="rounded-lg border border-gray-200 bg-white p-4 shadow animate-pulse"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-40 rounded bg-gray-200" />
                    <div className="h-3 w-56 rounded bg-gray-100" />
                  </div>
                  <div className="space-y-2 text-right">
                    <div className="ml-auto h-4 w-16 rounded bg-gray-200" />
                    <div className="ml-auto h-3 w-6 rounded bg-gray-100" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : upcomingRecurringLoadError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-5 text-center">
            <p className="text-sm text-red-700">{upcomingRecurringLoadError}</p>
            <button
              type="button"
              onClick={() => {
                loadUpcomingRecurringData({ forceRefresh: true }).catch(() => {
                  // Error state is handled in component state.
                });
              }}
              className="mt-3 rounded border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
            >
              Retry Future Load
            </button>
          </div>
        ) : upcomingRecurringGroups.length > 0 ? (
          <div className="space-y-3">
            {upcomingRecurringGroups.map((group) => {
              const headerTransaction = group.headerOccurrence;
              const groupKey = String(group.groupKey || '');
              const isExpanded = Boolean(expandedUpcomingRecurringByTemplate[groupKey]);
              const headerCategoryName =
                categoryDisplayNameById.get(String(headerTransaction.categoryId || '')) ||
                headerTransaction.category?.name ||
                'Uncategorized';
              const headerFrequencyRaw = String(headerTransaction.recurrenceFrequency || '').trim();
              const headerFrequency = headerFrequencyRaw
                ? `${headerFrequencyRaw.charAt(0).toUpperCase()}${headerFrequencyRaw.slice(1)}`
                : '';
              const headerRecurrenceRange = getGroupRecurrenceRange(group);
              const headerDate = toIsoDate(headerTransaction.transactionDate);

              return (
                <div key={`upcoming-group-${groupKey}`} className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow">
                  {isExpanded && (
                    <div className="flex items-center justify-between px-4 pb-1 pt-3">
                      <p className="inline-flex items-center gap-1.5 truncate text-xs text-gray-500">
                        <CalendarDaysIcon className="h-3.5 w-3.5 text-gray-400" />
                        {headerDate ? formatDate(headerDate) : 'Unknown date'}
                      </p>
                      <button
                        type="button"
                        onClick={() =>
                          handleStartEditRecurringTemplate(headerTransaction, headerTransaction.transactionDate)
                        }
                        disabled={Boolean(savingRecurringTemplateId)}
                        className="inline-flex h-7 items-center rounded-full bg-gray-100 px-3 text-xs font-medium text-gray-700 transition hover:bg-gray-200 disabled:opacity-60"
                        aria-label="Edit recurring occurrence"
                        title="Edit recurring occurrence"
                      >
                        Edit
                      </button>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedUpcomingRecurringByTemplate((current) => ({
                        ...current,
                        [groupKey]: !current[groupKey],
                      }))
                    }
                    className={`w-full p-4 text-left transition hover:bg-gray-50 ${isExpanded ? 'pt-2' : ''}`}
                  >
                    <div className="flex items-stretch justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-gray-900">{headerTransaction.storeName || 'Unknown Store'}</p>
                        <p className="mt-0.5 truncate text-sm text-gray-700">{headerTransaction.itemName || '-'}</p>
                        <p className="mt-0.5 truncate text-xs text-gray-600">
                          {headerCategoryName}
                          {headerRecurrenceRange ? ` | ${headerRecurrenceRange}` : ''}
                          {headerFrequency ? ` | ${headerFrequency}` : ''}
                        </p>
                      </div>
                      <div className="shrink-0 flex flex-col items-end justify-between text-right">
                        <p className="text-sm font-semibold text-gray-900">{formatCurrency(headerTransaction.itemTotal)}</p>
                        <span className="inline-flex items-center justify-end text-gray-500">
                          {isExpanded ? (
                            <ChevronUpIcon className="h-3 w-3" />
                          ) : (
                            <ChevronDownIcon className="h-3 w-3" />
                          )}
                        </span>
                      </div>
                    </div>
                  </button>

                  <div
                    className={`receipt-unroll-wrapper ${isExpanded ? 'receipt-unroll-wrapper-open' : ''}`}
                    aria-hidden={!isExpanded}
                  >
                    <div className="max-h-[62vh] overflow-y-auto p-3">
                      <div className="rounded-md border border-gray-200 bg-white">
                        <div className={`grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.35fr)_minmax(0,0.8fr)_auto] gap-2 border-b border-gray-200 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 sm:text-xs ${isExpanded ? 'receipt-unroll-line' : ''}`}>
                          <span>Frequency</span>
                          <span>Bill Due On</span>
                          <span className="text-right">Amount</span>
                          <span className="text-right">Edit</span>
                        </div>
                        <div className="divide-y divide-gray-200">
                          {group.occurrences.map((transaction, occurrenceIndex) => {
                            const normalizedFrequency = String(transaction.recurrenceFrequency || '').trim();
                            const formattedFrequency = normalizedFrequency
                              ? `${normalizedFrequency.charAt(0).toUpperCase()}${normalizedFrequency.slice(1)}`
                              : '-';
                            const billDueDate = formatBillDueDate(transaction.transactionDate) || '-';

                            return (
                              <div
                                key={`upcoming-${transaction.id}`}
                                className={`grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.35fr)_minmax(0,0.8fr)_auto] gap-2 px-3 py-2 text-xs text-gray-700 sm:text-sm ${isExpanded ? 'receipt-unroll-line' : ''}`}
                                style={isExpanded ? { animationDelay: `${Math.min(occurrenceIndex * 45, 360)}ms` } : undefined}
                              >
                                <span className="min-w-0 break-words">
                                  {formattedFrequency}
                                </span>
                                <span className="min-w-0 break-words">
                                  {billDueDate}
                                </span>
                                <span className="text-right font-semibold text-gray-900">
                                  {formatCurrency(transaction.itemTotal)}
                                </span>
                                <div className="text-right">
                                  <button
                                    type="button"
                                    onClick={() => handleStartEditRecurringTemplate(transaction, transaction.transactionDate)}
                                    disabled={Boolean(savingRecurringTemplateId)}
                                    className="inline-flex items-center p-0.5 text-gray-500 hover:text-blue-600 disabled:opacity-60"
                                    title="Edit this cycle and after"
                                    aria-label="Edit recurring occurrence"
                                  >
                                    <PencilSquareIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div
                          className={`flex items-center justify-between border-t border-gray-200 px-3 py-2 text-sm ${isExpanded ? 'receipt-unroll-line' : ''}`}
                          style={isExpanded ? { animationDelay: `${Math.min(group.occurrences.length * 45 + 60, 460)}ms` } : undefined}
                        >
                          <span className="font-semibold text-gray-700">Total Amount</span>
                          <span className="font-semibold text-gray-900">{formatCurrency(group.totalAmount)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
            <div className="text-4xl mb-3 text-gray-400">No Future Recurring Transactions</div>
            <p className="text-gray-600 text-base">
              {filtersAreActive
                ? 'No future recurring transactions match your current filters.'
                : 'Create a recurring transaction to see future occurrences here.'}
            </p>
          </div>
        )
      )}

      {editingRecurringTemplateId && (
        <div className="fixed inset-0 z-40 flex items-start justify-center bg-black/40 p-4 pt-20">
          <div className="w-full max-w-lg rounded-lg border border-gray-200 bg-white p-4 shadow-xl">
            <p className="text-sm font-semibold text-gray-900">
              {currentEditingRecurringTemplate?.storeName || 'Recurring Schedule'}
            </p>
            <p className="mt-1 text-xs text-gray-600">
              Apply changes starting from: {formatRecurrencePreviewDate(editingRecurringApplyFromDate) || '-'}
            </p>
            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
              <label className="text-xs text-gray-600">
                New Transaction Date
                <DatePicker
                  selected={editingRecurringTransactionDateValue}
                  onChange={(date) => {
                    if (!date) {
                      setEditingRecurringTransactionDate('');
                      return;
                    }
                    const timezoneOffsetMs = date.getTimezoneOffset() * 60000;
                    const localIsoDate = new Date(date.getTime() - timezoneOffsetMs).toISOString().split('T')[0];
                    setEditingRecurringTransactionDate(localIsoDate);
                  }}
                  dateFormat="MM-dd-yyyy"
                  withPortal
                  portalId="root"
                  wrapperClassName="mt-1 w-full"
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                  placeholderText="New Transaction Date"
                />
              </label>
              <label className="text-xs text-gray-600">
                Amount
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={editingRecurringAmount}
                  onChange={(event) => setEditingRecurringAmount(event.target.value)}
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                />
              </label>
              <label className="text-xs text-gray-600">
                Frequency
                <select
                  value={editingRecurringFrequency}
                  onChange={(event) => setEditingRecurringFrequency(event.target.value)}
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                >
                  <option value="">Select frequency</option>
                  {RECURRENCE_FREQUENCY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs text-gray-600">
                End Date
                <DatePicker
                  selected={editingRecurringEndDateValue}
                  onChange={(date) => {
                    if (!date) {
                      setEditingRecurringEndDate('');
                      return;
                    }
                    const timezoneOffsetMs = date.getTimezoneOffset() * 60000;
                    const localIsoDate = new Date(date.getTime() - timezoneOffsetMs).toISOString().split('T')[0];
                    setEditingRecurringEndDate(localIsoDate);
                  }}
                  minDate={editingRecurringTransactionDateValue || undefined}
                  dateFormat="MM-dd-yyyy"
                  withPortal
                  portalId="root"
                  wrapperClassName="mt-1 w-full"
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                  placeholderText="End Date"
                  isClearable
                />
              </label>
            </div>
            <div className="mt-3 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  handleDeleteRecurringSeries(editingRecurringTemplateId, editingRecurringApplyFromDate);
                }}
                disabled={Boolean(savingRecurringTemplateId)}
                className="mr-auto rounded border border-red-200 bg-white px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 disabled:opacity-60"
              >
                Delete Recurring
              </button>
              <button
                type="button"
                onClick={handleCancelEditRecurringTemplate}
                disabled={Boolean(savingRecurringTemplateId)}
                className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveRecurringTemplate}
                disabled={Boolean(savingRecurringTemplateId)}
                className="rounded bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {savingRecurringTemplateId ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isMobileView && showMobileAddButton && (
        <div ref={floatingAddMenuRef} className="fixed bottom-24 right-3 z-20 flex flex-col items-end gap-2">
          {activeAddMenu === 'floating' && (
            <div
              id="floating-add-menu"
              className="w-72 rounded-2xl border border-gray-200 bg-white/95 p-2 shadow-xl backdrop-blur"
            >
              <button
                type="button"
                onClick={() => {
                  setActiveAddMenu(null);
                  navigate('/upload');
                }}
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-left hover:bg-gray-50"
              >
                <p className="text-sm font-semibold text-gray-900">Upload Receipt</p>
                <p className="text-xs text-gray-500">Go to receipt upload and AI extraction flow.</p>
              </button>
              <button
                type="button"
                onClick={openManualModal}
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-left hover:bg-gray-50"
              >
                <p className="text-sm font-semibold text-gray-900">Add Transaction Detail</p>
                <p className="text-xs text-gray-500">Open a form to add transaction details.</p>
              </button>
              <button
                type="button"
                onClick={triggerHistoricalImportFilePicker}
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-left hover:bg-gray-50"
              >
                <p className="text-sm font-semibold text-gray-900">Import Historical Data</p>
                <p className="text-xs text-gray-500">Upload CSV or TSV from Excel, Sheets, or Numbers.</p>
              </button>
            </div>
          )}
          <button
            type="button"
            onClick={toggleFloatingAddMenu}
            aria-expanded={activeAddMenu === 'floating'}
            aria-controls="floating-add-menu"
            aria-label="Add transaction options"
            className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-600/95 text-white shadow-lg ring-1 ring-blue-500/40 transition hover:bg-blue-700 active:scale-95"
          >
            <PlusIcon className={`h-6 w-6 transition-transform ${activeAddMenu === 'floating' ? 'rotate-45' : ''}`} aria-hidden="true" />
          </button>
        </div>
      )}

      {showScrollTopButton && (
        <button
          type="button"
          onClick={scrollToTop}
          className={`fixed z-20 flex h-8 w-8 items-center justify-center rounded-full border border-gray-400/20 bg-gray-900/10 text-gray-700 hover:bg-gray-900/20 ${
            isMobileView ? 'bottom-40 right-4' : 'bottom-24 left-1/2 -translate-x-1/2'
          }`}
          aria-label="Scroll to top"
        >
          <ArrowUpIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      )}

      {activeActionTransaction && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4" onClick={closeTransactionActionMenu}>
          <div
            data-transaction-action-menu
            className="w-full max-w-md overflow-y-auto rounded-2xl border border-gray-200 bg-white p-4 shadow-xl max-h-[85vh]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-3">
              <p className="text-xs text-gray-500">Item</p>
              <p className="truncate text-sm font-medium text-gray-900" title={activeActionTransaction.itemName}>
                {activeActionTransaction.itemName}
              </p>
            </div>
            {quickCategoryIdsForTransaction(activeActionTransaction).length > 0 && (
              <div className="mb-3">
                <p className="mb-1 text-xs font-medium text-gray-500">Quick category</p>
                <div className="flex flex-wrap gap-1.5">
                  {quickCategoryIdsForTransaction(activeActionTransaction).map((categoryId) => (
                    <button
                      key={`quick-mobile-${activeActionTransaction.id}-${categoryId}`}
                      type="button"
                      onClick={async () => {
                        await handleCategoryChange(activeActionTransaction.id, categoryId);
                        closeTransactionActionMenu();
                      }}
                      className="rounded-full border border-gray-200 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
                    >
                      {categoryDisplayNameById.get(String(categoryId)) || 'Uncategorized'}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <label className="mb-1 block text-xs font-medium text-gray-600">Change category</label>
            <SearchableSelect
              value={String(activeActionTransaction.categoryId || '')}
              onChange={(selectedValue) => handleActionCategoryDropdownSelect(activeActionTransaction, selectedValue)}
              options={actionCategoryDropdownOptions}
              placeholder="Search main or subcategory"
              ariaLabel="Search category for selected transaction"
              inputClassName="w-full rounded border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100 focus:border-blue-400 focus:outline-none"
              menuClassName="max-h-64"
              disabled={updatingTransactionId === activeActionTransaction.id}
            />
            <button
              type="button"
              onClick={() => {
                handleActionCategoryDropdownSelect(activeActionTransaction, ADD_CATEGORY_OPTION);
              }}
              className="mt-2 w-full rounded border border-blue-200 bg-blue-50 px-3 py-2 text-left text-xs font-medium text-blue-700 hover:bg-blue-100"
            >
              + Add Category
            </button>
            <button
              type="button"
              onClick={() => {
                closeTransactionActionMenu();
                handleDelete(activeActionTransaction.id);
              }}
              className="mt-3 w-full rounded border border-gray-300 px-3 py-2 text-center text-sm text-red-600 hover:bg-red-50"
            >
              Delete
            </button>
            <button
              type="button"
              onClick={closeTransactionActionMenu}
              className="mt-2 w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {pendingDeleteToast && (
        <div className="fixed bottom-8 left-1/2 z-30 w-[min(92vw,28rem)] -translate-x-1/2 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-lg">
          <div className="flex items-center justify-between gap-2 text-sm">
            <p className="min-w-0 truncate text-gray-700">
              Deleted <span className="font-medium text-gray-900">{pendingDeleteToast.itemName}</span>
              {pendingDeletes.length > 1 ? ` (+${pendingDeletes.length - 1} more)` : ''}
            </p>
            <button
              type="button"
              onClick={() => undoDelete(pendingDeleteToast.id)}
              className="shrink-0 rounded border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
            >
              Undo
            </button>
          </div>
        </div>
      )}

      {showCreateCategoryModal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/55 p-4">
          <div className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 sm:px-5">
              <h3 className="text-lg font-medium text-gray-900">Create Category</h3>
              <button
                type="button"
                onClick={closeCreateCategoryModal}
                className="rounded border border-gray-300 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>

            <div className="space-y-3 overflow-y-auto px-4 py-3 sm:px-5 sm:py-4">
              {error && (
                <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              )}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Category Name</label>
                <div className="relative">
                  <input
                    type="text"
                    value={newCategoryForm.name}
                    onFocus={() => setIsCreateCategoryNameFocused(true)}
                    onBlur={() => setIsCreateCategoryNameFocused(false)}
                    onChange={(event) => {
                      setIsCreateCategorySelectedFromSuggestion(false);
                      setShowAllCreateSubcategoryRefs(false);
                      setNewCategoryForm((current) => ({ ...current, name: event.target.value }));
                    }}
                    placeholder="Type new main category or select existing"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {isCreateCategoryNameFocused && createCategoryNameSuggestions.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white p-1 shadow-lg">
                      {createCategoryNameSuggestions.map((category) => (
                        <button
                          key={`existing-main-transaction-${category.id}`}
                          type="button"
                          onMouseDown={(event) => {
                            event.preventDefault();
                            setIsCreateCategorySelectedFromSuggestion(true);
                            setShowAllCreateSubcategoryRefs(false);
                            setNewCategoryForm((current) => ({ ...current, name: String(category.name || '') }));
                            setIsCreateCategoryNameFocused(false);
                          }}
                          className="w-full rounded-md px-2.5 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50"
                        >
                          {category.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {!newCategoryForm.parentCategoryId && matchedMainCategoryForCreate && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-2.5">
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Existing Subcategories</p>
                  {existingCreateSubcategories.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {visibleCreateSubcategoryRefs.map((subcategory) => (
                        <span
                          key={`existing-subcategory-ref-transaction-${subcategory.id}`}
                          className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-700"
                        >
                          {subcategory.name}
                        </span>
                      ))}
                      {hiddenCreateSubcategoryRefCount > 0 && (
                        <button
                          type="button"
                          onClick={() => setShowAllCreateSubcategoryRefs((current) => !current)}
                          className="rounded-full border border-gray-300 bg-white px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-100"
                        >
                          {showAllCreateSubcategoryRefs ? 'Show less' : `+${hiddenCreateSubcategoryRefCount} more`}
                        </button>
                      )}
                    </div>
                  ) : (
                    <p className="mt-1 text-xs text-gray-500">No subcategories yet.</p>
                  )}
                </div>
              )}

              {!newCategoryForm.parentCategoryId && (
                <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-2.5">
                  <p className="mb-2 text-sm font-medium text-blue-900">Optional Subcategories</p>
                  <div className="flex min-h-11 flex-wrap items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2 py-1.5">
                    {newParentSubcategories.map((name) => (
                      <span key={`subcategory-chip-transaction-${name}`} className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-100 px-2 py-0.5 text-xs text-blue-800">
                        {name}
                        <button
                          type="button"
                          onClick={() => removeCreateSubcategoryChip(name)}
                          className="text-blue-700 hover:text-blue-900"
                          aria-label={`Remove ${name}`}
                        >
                          x
                        </button>
                      </span>
                    ))}
                    <input
                      type="text"
                      value={newSubcategoryInput}
                      onChange={(event) => setNewSubcategoryInput(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ',') {
                          event.preventDefault();
                          addCreateSubcategoryChip(newSubcategoryInput);
                          setNewSubcategoryInput('');
                        }
                      }}
                      onBlur={() => {
                        addCreateSubcategoryChip(newSubcategoryInput);
                        setNewSubcategoryInput('');
                      }}
                      placeholder={newParentSubcategories.length > 0 ? 'Add another subcategory' : 'Type subcategory and press Enter'}
                      className="min-w-[180px] flex-1 border-0 px-1 py-1 text-sm focus:outline-none"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="sticky bottom-0 flex items-center justify-end gap-3 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur sm:px-5">
              <button
                type="button"
                onClick={closeCreateCategoryModal}
                disabled={creatingCategory}
                className="text-sm font-medium text-gray-600 hover:text-gray-800 disabled:text-gray-400"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateCategoryFromModal}
                disabled={creatingCategory}
                className="rounded-lg bg-green-600 px-4 py-2 font-medium text-white hover:bg-green-700 disabled:bg-gray-400"
              >
                {creatingCategory ? 'Creating...' : 'Save Category'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ManualTransactionModal
        show={showManualModal}
        modalRef={manualModalRef}
        onClose={closeManualModal}
        onSubmit={submitManualTransaction}
        title={manualModalTitle}
        manualFormError={manualFormError}
        manualFieldErrors={manualFieldErrors}
        isMobileView={isMobileView}
        isManualDetailsOpen={isManualDetailsOpen}
        setIsManualDetailsOpen={setIsManualDetailsOpen}
        manualForm={manualForm}
        quickManualItem={quickManualItem}
        handleManualFormChange={handleManualFormChange}
        handleQuickManualItemChange={handleQuickManualItemChange}
        handleManualItemCategorySelect={handleManualItemCategorySelect}
        categorySelectOptions={categorySelectOptions}
        quickCategoryAriaLabel="Search category for quick transaction item"
        itemCategoryAriaLabelPrefix="Search category for transaction item"
        manualTransactionDate={manualTransactionDate}
        handleManualDateChange={handleManualDateChange}
        manualRecurrenceStartDate={manualRecurrenceStartDate}
        handleManualRecurrenceStartDateChange={handleManualRecurrenceStartDateChange}
        manualRecurrenceEndDate={manualRecurrenceEndDate}
        handleManualRecurrenceEndDateChange={handleManualRecurrenceEndDateChange}
        handleManualRecurringToggle={handleManualRecurringToggle}
        recurrenceFrequencyOptions={RECURRENCE_FREQUENCY_OPTIONS}
        manualRecurrencePreview={manualRecurrencePreview}
        handleManualItemChange={handleManualItemChange}
        removeManualItem={removeManualItem}
        addManualItem={addManualItem}
        resolveManualItemTotal={resolveManualItemTotal}
        manualItemsSubtotal={manualItemsSubtotal}
        manualTaxAmount={manualTaxAmount}
        manualGrandTotal={manualGrandTotal}
        formatCurrency={formatCurrency}
        creatingTransaction={creatingTransaction}
      />

      <input
        ref={historicalImportInputRef}
        type="file"
        accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values"
        onChange={handleHistoricalImportFileSelected}
        className="hidden"
      />

      <HistoricalImportModal
        show={showHistoricalImportModal}
        modalRef={historicalImportModalRef}
        onClose={closeHistoricalImportModal}
        onChooseFile={() => historicalImportInputRef.current?.click()}
        onImport={handleImportHistoricalTransactions}
        fileName={historicalImportFileName}
        importPreview={historicalImportPreview}
        fallbackCategoryId={historicalImportFallbackCategoryId}
        onFallbackCategoryChange={setHistoricalImportFallbackCategoryId}
        categoryOptions={historicalImportCategoryOptions}
        importing={importingHistorical}
      />

      {showManualDuplicateConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4">
          <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-5 shadow-2xl">
            <h3 className="text-base font-semibold text-gray-900">Possible Duplicate Receipt</h3>
            <p className="mt-2 text-sm text-gray-600">This manual entry looks similar to an existing receipt.</p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowManualDuplicateConfirm(false)}
                className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowManualDuplicateConfirm(false);
                  saveManualTransaction(true);
                }}
                className="rounded bg-amber-600 px-3 py-2 text-sm text-white hover:bg-amber-700"
              >
                Save Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      <StatusToast
        error={error}
        success={success}
        onClearError={() => setError('')}
        onClearSuccess={() => setSuccess('')}
      />
    </div>
  );
}

export default TransactionList;

